import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
// Este import NO es solo por las cuatro funciones de lectura: importar `api/levantamiento` es lo que
// REGISTRA el enviador de la cola (`setVisitaUploader`, al final de ese archivo). Sin él,
// `flushVisitas` lanzaría «la cola no tiene enviador registrado» en el primer guardado.
import {
  listCampaniasLevantamiento, listMarcasLevantamiento, listMercadosLevantamiento, listMisVisitas,
  listPresentacionesDeMarcas,
  type CampaniaLevantamiento, type MercadoLevantamiento, type VisitaPropiaRow,
} from '../../api/levantamiento';
import {
  enqueueVisita, flushVisitas, listFailedVisitas, pendingVisitaCount, reencolarVisitasFallidas,
  visitasLocalesDe,
  type EstadoEnvioVisita, type ResumenVisitaLocal,
} from '../../offline/visitaQueue';
import { compressImage } from '../../lib/compressImage';
import CampoFoto from '../../components/CampoFoto';
import { useAuth } from '../../components/AuthProvider';
import { getGpsFix } from './Marcar';
import {
  CATEGORIAS, CLIENTES, DE_QUIEN, MARCAS_RECOMENDABLES, MERCADO_OTRO, MOTIVOS_NO_PUDE,
  MOTIVOS_RECOMENDACION, PRECIOS_POR, RANGO_PRECIO_POR_CATEGORIA, RECOMENDADA_OTRA,
  claveDeMercado, avisoPrecioPorUnidad, avisoRangoPrecio, borradorVacio, etiquetaCategoria,
  filaOfreceUnidades,
  etiquetaEstadoVisita, filaPrecioVacia, filaPromocionVacia, fotosDelBorrador, loQueFaltaEnFicha,
  marcasDeCategorias, marcasQueProponenPrecio, numeroYaUsado, tituloDePuesto, visitaDesdeBorrador,
  type BorradorFicha, type CategoriaProducto, type EstadoVisita, type FilaPrecio,
  type FilaPromocion, type MarcaLevantamiento, type Presencia, type PresentacionMarca,
  type SeccionObservable,
} from '../../domain/levantamiento';

const TZ = 'America/Lima';

/**
 * Las tres respuestas de la cuadrícula **con el idioma del campo**, y en el orden en que ocurren.
 *
 * ⚠️ NO se usa `PRESENCIAS` del dominio a propósito, y no es un descuido: sus etiquetas son las
 * viejas —«Lo trabaja pero está agotado»— y el primer día real de medición salieron **cero quiebres
 * en 14 puestos** mientras el texto libre de las observaciones decía dos veces *«se le acabó»*. La
 * respuesta existía en el puesto y no entraba por el formulario, porque la palabra que la nombraba
 * no es la que usa la gente. Cambiar el valor guardado sería otra cosa —esos tres valores viajan al
 * servidor y los lee el panel— así que lo que cambia es únicamente la etiqueta.
 *
 * El ORDEN también cambia: «Se le acabó» va al medio, entre «Tiene» y «No la trabaja», porque es lo
 * que dice: la marca sí entra al puesto pero hoy no está. Antes iba última, después de la respuesta
 * que la niega, y en una fila de tres botones eso se lee como la opción rara.
 */
export const OPCIONES_PRESENCIA: { valor: Presencia; label: string }[] = [
  { valor: 'presente', label: 'Tiene' },
  { valor: 'quiebre', label: 'Se le acabó' },
  { valor: 'no_trabaja', label: 'No la trabaja' },
];

/** Botón de opción del tamaño que exige un dedo, no un puntero. */
function Opcion(
  { activo, onClick, children, centrado = false }:
  { activo: boolean; onClick: () => void; children: React.ReactNode; centrado?: boolean },
) {
  return (
    <button type="button" onClick={onClick} aria-pressed={activo}
      className={`min-h-[44px] rounded border py-2 text-sm ${
        centrado ? 'px-2 text-center' : 'px-3 text-left'
      } ${
        activo ? 'border-blue-600 bg-blue-50 font-medium text-blue-900' : 'border-gray-300'
      }`}>
      {children}
    </button>
  );
}

/**
 * Un campo que empieza como ENLACE y recién se despliega si hace falta.
 *
 * Es el mismo criterio que ya tenía la observación de cada sección, ahora reutilizable: la ficha
 * apunta a 4-5 minutos por puesto en un recorrido de treinta, y un campo opcional siempre abierto
 * le cuesta pantalla —y lectura— a TODOS los puestos a cambio de algo que la mayoría no llena. Al
 * estar plegado cuesta cero toques cuando no se usa, que es la cuenta que decide si esta pantalla
 * sirve.
 *
 * `abiertoInicial` existe para que un campo que YA tiene valor no se esconda: un dato escrito y no
 * visible es un dato que nadie corrige.
 */
function Plegable(
  { etiqueta, abiertoInicial = false, children }:
  { etiqueta: string; abiertoInicial?: boolean; children: React.ReactNode },
) {
  const [abierto, setAbierto] = useState(abiertoInicial);
  if (abierto) return <>{children}</>;
  return (
    <button type="button" onClick={() => setAbierto(true)}
      className="mt-3 min-h-[44px] text-left text-sm text-blue-600 underline">
      {etiqueta}
    </button>
  );
}

/**
 * Observación libre de una sección. Empieza como un ENLACE, no como un campo.
 *
 * La ficha apunta a 4-5 minutos y el riesgo mayor del levantamiento es que el auditor deje de
 * medir a las dos semanas. Ocho cajas de texto siempre abiertas alargan la pantalla para todos
 * a cambio de algo que la mayoría de los puestos no necesita.
 */
function Observacion({ valor, onChange }: { valor: string; onChange: (v: string) => void }) {
  return (
    <Plegable etiqueta="+ Agregar observación" abiertoInicial={valor !== ''}>
      <label className="mt-3 flex flex-col text-sm">
        Observación <span className="text-xs text-gray-500">(opcional)</span>
        <textarea rows={2} className="mt-1 rounded border px-3 py-2 text-base"
          placeholder="Lo que no entra en las opciones de arriba"
          value={valor} onChange={(e) => onChange(e.target.value)} />
      </label>
    </Plegable>
  );
}

function Bloque(
  { titulo, children, observacion }:
  { titulo: string; children: React.ReactNode; observacion?: React.ReactNode },
) {
  return (
    <section className="mb-5 rounded-lg border bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
        {titulo}
      </h2>
      {children}
      {observacion}
    </section>
  );
}

/** Cómo se transforma la lista de fotos de un campo (agregar al final, quitar una…). */
type MezclaFotos = (ya: Blob[]) => Blob[];
/** Dónde se aplica esa transformación: la foto del puesto, la de una fila de precio, etc. */
type DestinoFotos = (mezcla: MezclaFotos) => void;

/** Quita la foto de esa posición. Pura y exportada para poder probarla sin montar la pantalla. */
export function quitarEn(fotos: readonly Blob[], indice: number): Blob[] {
  return fotos.filter((_, i) => i !== indice);
}

/**
 * Comprime las fotos recién elegidas, UNA POR UNA y en el orden en que vinieron.
 *
 * En secuencia y no con `Promise.all`: cada compresión dibuja la foto en un canvas, y cinco fotos
 * de 12 Mpx a la vez es el tipo de pico de memoria que en un celular de gama baja termina con la
 * pestaña cerrada —y con la ficha a medio llenar adentro—. El orden importa además porque es el
 * que se conserva hasta la ruta de cada archivo en el Storage.
 */
async function comprimirTodas(files: readonly File[]): Promise<Blob[]> {
  const out: Blob[] = [];
  for (const f of files) out.push(await compressImage(f));
  return out;
}

/**
 * Las fotos que ya lleva un campo: cuántas son, cómo se ven y el botón para quitar cada una.
 *
 * **POR QUÉ TIENE QUE PODERSE QUITAR.** Sin esto, una foto movida, oscura o del puesto de al lado
 * queda pegada a la ficha para siempre: el auditor no tiene ninguna salida —no puede reemplazarla,
 * porque ahora las fotos se SUMAN— y el que revisa recibe evidencia equivocada mezclada con la
 * buena, sin saber cuál es cuál.
 *
 * ⚠️ **LAS URL SE REVOCAN.** `URL.createObjectURL` retiene el blob completo hasta que se revoca la
 * URL o se cierra la pestaña; el recolector de basura no lo alcanza solo. Son fotos de cámara: en
 * un recorrido de treinta puestos con varias fotos cada uno, no revocarlas son cientos de
 * megabytes retenidos y el navegador del celular termina matando la pestaña. Se revocan al cambiar
 * la lista y al desmontar, que es lo mismo que se declara una sola vez en el `return` del efecto.
 */
function FotosElegidas(
  { fotos, nombre, onQuitar }:
  { fotos: Blob[]; nombre: string; onQuitar: (indice: number) => void },
) {
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    const nuevas = fotos.map((f) => URL.createObjectURL(f));
    setUrls(nuevas);
    return () => { for (const u of nuevas) URL.revokeObjectURL(u); };
  }, [fotos]);

  if (fotos.length === 0) return null;
  return (
    <div className="mt-2">
      <p className="text-xs text-green-700">
        {fotos.length === 1 ? '1 foto lista.' : `${fotos.length} fotos listas.`}
      </p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {fotos.map((_, i) => (
          // La clave es la posición porque un `Blob` no tiene id y todo lo que se pinta sale de
          // esa misma posición: no hay estado propio de la miniatura que se pueda cruzar.
          <li key={i} className="w-20">
            {/* En el render inmediatamente posterior a agregar una foto la URL todavía no está
                creada (la crea el efecto). Se pinta el hueco en vez de un `img` sin `src`, que
                el navegador muestra como imagen rota. */}
            {urls[i] === undefined
              ? <div className="h-20 w-20 rounded border bg-gray-100" />
              : (
                <img src={urls[i]} alt={`${nombre}, foto ${i + 1}`}
                  className="h-20 w-20 rounded border object-cover" />
              )}
            <button type="button" onClick={() => onQuitar(i)}
              className="mt-1 min-h-[32px] w-full text-xs text-red-600 underline">
              quitar
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Campo de foto de la ficha: VARIAS fotos, con los dos caminos de `CampoFoto` —tomarla ahora o
 * elegirla de la galería del celular—.
 *
 * **Por qué varias** (2026-08-19, pedido del usuario: *"en todos los campos donde se puede subir
 * fotos, se puedan subir más de una"*): el frente de un puesto no entra en una toma, y el precio
 * se lee en el cartel y en la etiqueta. Con un solo `Blob` la segunda foto reemplazaba a la
 * primera **sin avisar**, así que la evidencia se perdía en silencio.
 *
 * **Las nuevas se SUMAN, nunca reemplazan.** Elegir tres y después una cuarta deja cuatro: es el
 * comportamiento que hace que la cámara —que devuelve una foto por disparo— sirva para ir
 * juntándolas de a una.
 *
 * **Por qué acá SÍ se acepta la galería** (antes estos campos forzaban `capture` y escondían la
 * galería, con el argumento de que era "evidencia de terreno, no un archivo cualquiera"): en la
 * ficha la foto es un DATO —cómo se ve el precio, el empaque, el cartel de la promoción—, no la
 * prueba de dónde estaba la persona. Lo que ata la ficha al terreno es el GPS que se pide al
 * guardar, no el origen del archivo. Y forzar la cámara sí rompía casos reales: mercados donde
 * el puestero no deja fotografiar mientras uno está parado ahí y la foto se toma al salir, o un
 * celular que devuelve la cámara con error y deja la ficha sin evidencia. El usuario lo pidió
 * expresamente: *"que se pueda escoger de la memoria del celular, ya que está limitado a usar
 * la cámara"*.
 *
 * ⚠️ En el MARCADO de asistencia (`Marcar.tsx`) la decisión es la contraria y está cerrada: ahí
 * la foto no es un dato sino la prueba de que la persona estaba en la tienda a esa hora, atada
 * al GPS y al sello. Con galería se podría marcar desde la casa con una foto de otro día, que es
 * justo lo que la geocerca existe para impedir. Ese input se queda solo con cámara.
 */
function CampoFotos(
  { etiqueta, nombre, fotos, onAgregar, onQuitar }:
  {
    etiqueta: string; nombre: string; fotos: Blob[];
    onAgregar: (files: File[]) => void; onQuitar: (indice: number) => void;
  },
) {
  return (
    <div className="mt-2">
      <CampoFoto
        etiqueta={etiqueta}
        nombre={nombre}
        varias
        // La cuenta la pinta `FotosElegidas`, que además dice CUÁNTAS son y deja quitarlas. El
        // «Foto lista.» de `CampoFoto` sobraría y, peor, mentiría cuando hay tres.
        listaTexto=""
        lista={false}
        onElegir={(e) => {
          const input = e.target;
          onAgregar(Array.from(input.files ?? []));
          // Se vacía el input para que volver a elegir EL MISMO archivo dispare `change` otra vez:
          // sin esto, quitar una foto por error y volver a elegirla de la galería no hace nada, y
          // desde la pantalla se ve como si el botón estuviera roto. Los `File` ya se copiaron a
          // un arreglo propio, así que vaciarlo no se lleva nada.
          input.value = '';
        }}
      />
      <FotosElegidas fotos={fotos} nombre={nombre} onQuitar={onQuitar} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Las reglas del rediseño por categorías, puras y exportadas
// ---------------------------------------------------------------------------

/**
 * Tilda o destilda una categoría **conservando el orden canónico** de `CATEGORIAS`.
 *
 * El orden importa porque `categorias` se guarda como `text[]` y así se lee después: si dependiera
 * del orden en que la auditora tocó las casillas, dos puestos que venden lo mismo quedarían con
 * `{panales,papel_higienico}` y `{papel_higienico,panales}`, y agrupar por «qué vende» tendría que
 * ordenar el arreglo antes de comparar — o daría dos grupos donde hay uno.
 */
export function alternarCategoria(
  categorias: readonly CategoriaProducto[], c: CategoriaProducto,
): CategoriaProducto[] {
  if (categorias.includes(c)) return categorias.filter((x) => x !== c);
  return CATEGORIAS.map((x) => x.valor).filter((v) => v === c || categorias.includes(v));
}

/**
 * Qué declaró el puesto ANTERIOR de este mercado, para ofrecerlo de un toque.
 *
 * ## Por qué existe
 *
 * El rediseño por categorías cambió una pregunta de opción única (`giro`) por casillas, y ahí se
 * coló el defecto que la revisión adversarial marcó como bloqueante: el puesto que vende papel Y
 * pañales —el caso más común de los que declaran categoría— pasó de **un** toque («Ambos») a
 * **dos**. Un formulario que existe para pedir menos toques no puede pedir uno más.
 *
 * El dato dice que la salida no es adivinar sino repetir: en producción, **91 de 124 puestos
 * medidos (73 %) declararon exactamente lo mismo que el puesto anterior** del mismo mercado, día y
 * auditor (medido el 2026-08-22 sobre 174 visitas). Tiene sentido: un recorrido va por pasillos, y
 * los pasillos agrupan giros.
 *
 * ## Por qué se OFRECE y no se PRECARGA
 *
 * Precargar las casillas dejaría la ficha lista sin que nadie declarare nada, y `categorias` no es
 * una etiqueta: es **el alcance de lo que se preguntó**. Un puesto que quedó con «pañales» heredado
 * y sin ninguna marca presente es indistinguible de uno que de verdad no trabaja ninguna — el mismo
 * argumento con el que la `0060` dejó `abarrotes` sin traducir y con el que `numero_recorrido` dejó
 * de autoincrementarse (§3: «basta que se le olvide corregirlo UNA vez»).
 *
 * Ofrecerlo como botón conserva la declaración —el auditor toca y afirma— y cuesta **un** toque,
 * que es lo mismo que costaba «Ambos» antes. Nunca es peor que el formulario viejo, y en el 73 % de
 * los puestos es igual de rápido sin importar cuántas categorías tenga la campaña.
 *
 * Devuelve `[]` cuando no hay de dónde copiar: primer puesto del mercado, o un anterior `no_pude`
 * (que no declara nada) o guardado por una versión anterior de la app.
 */
export function categoriasDelPuestoAnterior(
  avanceLocal: readonly ResumenVisitaLocal[],
): CategoriaProducto[] {
  let ultima: ResumenVisitaLocal | null = null;
  for (const v of avanceLocal) {
    // El `?? []` NO es por el tipo —`categorias` es obligatoria en `ResumenVisitaLocal`— sino por
    // las fichas que ya estaban guardadas en el celular ANTES de que este campo existiera: la cola
    // sobrevive a las actualizaciones de la app, así que ahí llega sin la clave y un `.length`
    // directo tumbaría la ficha entera. Es la misma trampa que `nombrePuesto` dejó documentada.
    if (v.estado !== 'medido' || (v.categorias ?? []).length === 0) continue;
    // `>=` y no `>`: la lista llega ordenada por hora de guardado, así que ante dos fichas en el
    // mismo milisegundo gana la última del recorrido, que es la que el auditor acaba de cerrar.
    if (ultima === null || v.guardadaEn >= ultima.guardadaEn) ultima = v;
  }
  return ultima === null ? [] : [...(ultima.categorias ?? [])];
}

/**
 * ¿Esta fila de precio está vacía de verdad? (o sea: se puede retirar sola sin llevarse nada)
 *
 * Mira los SEIS campos, y cada uno está por una razón concreta. Antes miraba solo tres —precio,
 * presentación de texto y fotos— y con las presentaciones del catálogo eso pasó a ser un error:
 * elegir «XXG x44» de la lista y después corregir la cuadrícula retiraba la fila con la
 * presentación adentro, y la auditora la veía desaparecer sin ningún aviso.
 *
 * Las fotos cuentan como dato aunque no haya ningún texto: retirar sola una fila con fotos ya
 * tomadas se llevaría la evidencia por la que alguien se agachó.
 */
export function filaDePrecioSinDatos(p: FilaPrecio): boolean {
  return p.precio.trim() === '' && p.presentacion.trim() === '' && p.presentacionId === ''
    && p.marcaLibre.trim() === '' && p.unidades.trim() === '' && p.fotos.length === 0;
}

/**
 * El borrador RECORTADO a lo que las categorías declaradas sostienen. Es lo que se guarda.
 *
 * ⚠️ POR QUÉ EXISTE, y qué error concreto evita. `presencias` y `precios` se acumulan mientras dura
 * la ficha: si la auditora tilda «Pañales», contesta las dos marcas y después se da cuenta de que
 * se equivocó de puesto y lo destilda, esas dos respuestas siguen adentro del borrador. Sin este
 * recorte viajarían igual, y el resultado sería una visita que declara «este puesto vende solo
 * papel» y trae dos mediciones de pañal — o sea, exactamente la ambigüedad que las categorías
 * vienen a quitar, pero ahora escrita en la base como si fuera un hecho observado.
 *
 * Se aplica a las TRES salidas a la vez (`loQueFaltaEnFicha`, `visitaDesdeBorrador` y
 * `fotosDelBorrador`) porque son la misma ficha vista de tres maneras: si las fotos se calcularan
 * sobre el borrador completo, se subirían al Storage archivos de filas que el payload ya no
 * menciona, y quedarían huérfanos en el bucket sin que nada falle.
 *
 * Las filas de marca TECLEADA (`marcaId === ''`) se conservan siempre: no pertenecen a ninguna
 * categoría de la cuadrícula —son el camino para lo que no está configurado, y hoy toda toalla
 * higiénica pasa por ahí—, así que recortarlas por categoría las borraría a todas.
 *
 * NO destruye nada en la pantalla: el borrador de verdad conserva las respuestas y volver a tildar
 * la categoría las devuelve. Lo que este recorte decide es qué se GUARDA, y mientras tanto la
 * pantalla avisa de lo que quedó afuera (ver `categoriasNoDeclaradasConDatos`).
 */
export function borradorEfectivo(
  b: BorradorFicha, marcasVisibles: readonly MarcaLevantamiento[],
): BorradorFicha {
  // Una «no pude» ya sale vacía de todo en `visitaDesdeBorrador`; recortarla acá no cambiaría nada
  // y solo agregaría un camino más que mantener.
  if (b.estado === 'no_pude') return b;
  const visibles = new Set(marcasVisibles.map((m) => m.id));
  const presencias: Record<string, Presencia | ''> = {};
  for (const [id, v] of Object.entries(b.presencias)) {
    if (visibles.has(id)) presencias[id] = v;
  }
  return {
    ...b,
    presencias,
    precios: b.precios.filter((p) => p.marcaId === '' || visibles.has(p.marcaId)),
  };
}

/**
 * Las categorías que tienen respuestas anotadas y que la ficha **ya no declara**. Alimenta el aviso.
 *
 * Es la contracara visible de `borradorEfectivo`: ese recorte es correcto pero silencioso, y algo
 * que se deja de guardar sin decirlo es lo mismo que perderlo. Con esto la pantalla puede nombrar
 * la categoría —«Pañales»— y explicar que volver a tildarla recupera lo contestado, que es la
 * salida que la auditora necesita si destildó por error.
 */
export function categoriasNoDeclaradasConDatos(
  b: BorradorFicha, marcas: readonly MarcaLevantamiento[],
): CategoriaProducto[] {
  const conDatos = new Set<CategoriaProducto>();
  for (const m of marcas) {
    if (b.categorias.includes(m.categoria)) continue;
    const respondida = b.presencias[m.id] !== '' && b.presencias[m.id] !== undefined;
    const conPrecio = b.precios.some((p) => p.marcaId === m.id && !filaDePrecioSinDatos(p));
    if (respondida || conPrecio) conDatos.add(m.categoria);
  }
  return CATEGORIAS.map((c) => c.valor).filter((v) => conDatos.has(v));
}

/** Un bloque de filas de precio con su rótulo. `declarada = false` = quedó fuera de lo que se guarda. */
export interface GrupoDePrecios {
  clave: string;
  titulo: string;
  declarada: boolean;
  filas: FilaPrecio[];
}

/** Clave del grupo de las filas cuya marca la tecleó la auditora. */
export const GRUPO_OTROS = 'otros';

/**
 * Las filas de precio AGRUPADAS POR CATEGORÍA, que es como el usuario pidió ver el formulario:
 * *«tener esos campos separados por categoría de producto»*.
 *
 * Solo se emiten los grupos que tienen al menos una fila. Tres encabezados con «todavía ninguno»
 * debajo alargarían la pantalla con la misma información que da su ausencia, y el vacío total ya
 * tiene su propio texto en la pantalla.
 *
 * Un grupo puede salir con `declarada: false`: son las filas de una categoría que la ficha ya no
 * declara y que por eso NO se van a guardar (ver `borradorEfectivo`). Se muestran igual —con su
 * aviso— en vez de desaparecer: una fila con precio y fotos que se esfuma de la pantalla se lee
 * como que la aplicación se comió el trabajo.
 *
 * El orden de las filas dentro de cada grupo es el `sort` de la marca y no el de creación, para que
 * la lista de precios se lea en el mismo orden que la cuadrícula de arriba. Las filas de la misma
 * marca —el puesto que vende suelto Y por paquete— conservan entre sí el orden en que se agregaron.
 */
export function gruposDePrecio(
  precios: readonly FilaPrecio[], marcas: readonly MarcaLevantamiento[],
  categorias: readonly CategoriaProducto[],
): GrupoDePrecios[] {
  const porId = new Map(marcas.map((m) => [m.id, m]));
  const salida: GrupoDePrecios[] = [];
  for (const c of CATEGORIAS) {
    const filas = precios
      .filter((p) => porId.get(p.marcaId)?.categoria === c.valor)
      .sort((a, z) => (porId.get(a.marcaId)?.sort ?? 0) - (porId.get(z.marcaId)?.sort ?? 0));
    if (filas.length === 0) continue;
    salida.push({
      clave: c.valor, titulo: c.label, declarada: categorias.includes(c.valor), filas,
    });
  }
  const libres = precios.filter((p) => !porId.has(p.marcaId));
  if (libres.length > 0) {
    salida.push({
      clave: GRUPO_OTROS, titulo: 'Otros productos que anotaste', declarada: true, filas: libres,
    });
  }
  return salida;
}

/** Las presentaciones del catálogo de esa marca, ya en el orden con el que vinieron de la base. */
export function presentacionesDeMarca(
  presentaciones: readonly PresentacionMarca[], marcaId: string,
): PresentacionMarca[] {
  if (marcaId === '') return [];
  return presentaciones.filter((p) => p.marcaId === marcaId);
}

/** Cuántas unidades trae la presentación elegida, o `null` si no eligió ninguna o no aplica. */
export function unidadesDePresentacion(
  presentaciones: readonly PresentacionMarca[], presentacionId: string,
): number | null {
  if (presentacionId === '') return null;
  return presentaciones.find((p) => p.id === presentacionId)?.unidades ?? null;
}

/**
 * Aviso de rango para la fila cuya marca NO está en la cuadrícula.
 *
 * `avisoRangoPrecio` lee el rango de la marca y devuelve `null` cuando no hay marca, así que una
 * fila tecleada a mano no tenía ninguna bandera de precio. Con las categorías eso dejó de ser un
 * caso raro: `toallas_higienicas` no tiene ni una marca configurada, o sea que **todo precio de
 * toallas se teclea**, y sin esto sería la única categoría entera sin control de rango.
 *
 * ⚠️ SOLO SE PRONUNCIA CUANDO LA FICHA DECLARÓ UNA SOLA CATEGORÍA, y esa es la limitación honesta:
 * la fila no guarda a qué categoría pertenece —`FilaPrecio` no tiene ese campo y el esquema
 * tampoco—, así que en un puesto que vende papel Y pañales no hay forma de saber si el S/45 que
 * acaba de teclear es un fardo de papel (alto) o un paquete de pañales (normal). Avisar con el
 * rango equivocado es peor que no avisar: una bandera que salta con un precio legítimo se aprende a
 * ignorar, y ahí se pierde entera.
 */
export function avisoRangoDeFilaTecleada(
  precio: string, categorias: readonly CategoriaProducto[],
): string | null {
  if (categorias.length !== 1) return null;
  const v = Number(precio);
  if (precio.trim() === '' || !Number.isFinite(v) || v <= 0) return null;
  const categoria = categorias[0];
  const { min, max } = RANGO_PRECIO_POR_CATEGORIA[categoria];
  const nombre = etiquetaCategoria(categoria).toLowerCase();
  if (v < min) {
    return `S/ ${v.toFixed(2)} es más bajo de lo esperado para ${nombre} (desde S/ ${min.toFixed(2)}).`;
  }
  if (v > max) {
    return `S/ ${v.toFixed(2)} es más alto de lo esperado para ${nombre} (hasta S/ ${max.toFixed(2)}).`;
  }
  return null;
}

/**
 * ¿El nombre de la marca nueva parece una LISTA de marcas? Avisa, no bloquea.
 *
 * ⚠️ Es el defecto exacto que se encontró en el primer día real: en un puesto entró
 * **«Royal, doñita y vitale»** en el campo de una sola marca desconocida. Tres marcas en una fila
 * no se pueden contar, no se pueden buscar y no se pueden dar de alta: el hallazgo, que existe
 * justamente para descubrir lo que nadie puso en la lista, quedó inutilizable.
 *
 * Mira solo separadores INEQUÍVOCOS —coma, punto y coma, barra—. Se descartó incluir « y » aunque
 * es el que aparece en el caso real: hay marcas que lo llevan en el nombre, y un aviso que salta
 * con un nombre legítimo enseña a ignorarlo. El ejemplo real cae igual, por la coma.
 */
export function avisoVariasMarcasEnUnCampo(nombre: string): string | null {
  if (!/[,;/]/.test(nombre.trim())) return null;
  return 'Parece que anotaste más de una marca. Acá va UNA sola: elige la que más te llamó la '
    + 'atención y anota las otras en la observación o como precio, cada una en su fila.';
}

/**
 * Una fila de precio: precio, cómo lo vende, presentación, unidades y sus fotos.
 *
 * **Es un componente propio y de nivel de módulo por dos razones, y ninguna es de orden.**
 *
 * 1. Necesita estado PROPIO: «elegí Otra presentación» no se puede deducir del borrador. Con el
 *    catálogo puesto, `presentacionId === ''` significa a la vez «todavía no elegí ninguna» y «elegí
 *    Otra y aún no escribí nada», y son dos pantallas distintas —la segunda muestra la caja de
 *    texto—. Guardarlo en el borrador ensuciaría el payload con un dato que no viaja; guardarlo en
 *    la pantalla padre obligaría a un `Record<idDeFila, boolean>` paralelo que hay que acordarse de
 *    limpiar cuando se quita una fila.
 * 2. **Declarado ADENTRO de `FichaPuesto` sería un componente nuevo en cada render**: React lo
 *    trataría como otro tipo, desmontaría la fila entera en cada tecla y el foco se perdería
 *    después de cada dígito del precio. Es la regla del proyecto y acá es donde muerde.
 *
 * `presentaciones` llega YA filtrada por marca: el filtrado es una regla y vive en
 * `presentacionesDeMarca`, no adentro del pintado.
 */
function FilaDePrecio(
  { fila, marca, presentaciones, categorias, onCambio, onQuitar, onOtroPrecio, onFotos, onQuitarFoto }:
  {
    fila: FilaPrecio;
    marca: MarcaLevantamiento | undefined;
    presentaciones: readonly PresentacionMarca[];
    categorias: readonly CategoriaProducto[];
    onCambio: (cambio: Partial<FilaPrecio>) => void;
    onQuitar: () => void;
    onOtroPrecio: () => void;
    onFotos: (files: File[]) => void;
    onQuitarFoto: (indice: number) => void;
  },
) {
  const sinCatalogo = presentaciones.length === 0;
  /** Eligió «Otra». Arranca encendido si la fila ya trae una presentación escrita a mano. */
  const [otra, setOtra] = useState(fila.presentacionId === '' && fila.presentacion !== '');
  const escribiendo = sinCatalogo || otra;
  const unidadesCatalogo = unidadesDePresentacion(presentaciones, fila.presentacionId);
  // Las unidades se piden solo cuando NO hay de dónde sacarlas y hay algo que dividir. Volver a
  // preguntarlas cuando la presentación del catálogo ya las trae es una pregunta de más por fila de
  // precio; pedirlas cuando el precio declarado es el de UNA unidad es pedir que se invente el
  // contenido de un paquete que ni se está mirando. Es la misma condición que `loQueFaltaEnFicha`
  // usa para exigirlas: si se separan, la pantalla esconde un campo que la validación reclama.
  const pideUnidades = filaOfreceUnidades(marca, fila.precioPor, unidadesCatalogo);
  const ppu = avisoPrecioPorUnidad(fila.precio, fila.unidades, fila.precioPor, unidadesCatalogo);
  const rango = marca === undefined
    ? avisoRangoDeFilaTecleada(fila.precio, categorias)
    : avisoRangoPrecio(marca, fila.precio);
  const nombre = marca?.nombre ?? 'Otro producto';

  return (
    <div className="rounded border border-gray-200 p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-sm font-medium">{nombre}</p>
        <button type="button" onClick={onQuitar}
          className="min-h-[32px] text-xs text-red-600 underline">quitar</button>
      </div>

      {/* La «Marca económica» y las filas libres piden de qué marca son. */}
      {(marca?.esReferenciaBarata || fila.marcaId === '') && (
        <label className="flex flex-col text-sm">
          ¿De qué marca es?
          <input className="mt-1 min-h-[44px] rounded border px-3 text-base"
            value={fila.marcaLibre}
            onChange={(e) => onCambio({ marcaLibre: e.target.value })} />
        </label>
      )}

      <label className="mt-2 flex flex-col text-sm">
        Precio (S/)
        <input type="number" inputMode="decimal" step="0.10" min={0}
          className="mt-1 min-h-[44px] rounded border px-3 text-base"
          value={fila.precio}
          onChange={(e) => onCambio({ precio: e.target.value })} />
      </label>

      {/* ⚠️ LA PREGUNTA QUE FALTABA. El campo «precio» significaba las dos cosas a la vez y la MISMA
          auditora lo usó de las dos formas el mismo día: en siete filas de Babysec anotó el precio
          del PAQUETE (S/44.00 el de 44 unidades) y en una anotó S/1.00 declarando 57 unidades, que
          es lo que cuesta un pañal suelto — dividir esa fila da S/0.018 por pañal, un número que no
          existe. Nace en «paquete», que es lo que la columna ya significaba de hecho, así que el
          caso frecuente cuesta CERO toques y el raro cuesta uno. */}
      <p className="mb-1 mt-3 text-sm">¿Cómo lo vende?</p>
      <div className="grid grid-cols-2 gap-2">
        {PRECIOS_POR.map((o) => (
          <Opcion key={o.valor} centrado activo={fila.precioPor === o.valor}
            onClick={() => onCambio({ precioPor: o.valor })}>
            {o.label}
          </Opcion>
        ))}
      </div>

      {/* PRESENTACIÓN: lista CERRADA de esa marca. Era texto libre y produjo NUEVE escrituras para
          dos presentaciones —«4», «X4», «XL X4», «XL x 4», «Elite XL X4» para el mismo paquete de 4
          de Elite—. Un campo que se CUENTA no puede ser texto libre.
          Botones y no un desplegable: el botón es UN toque y el `select` son dos (abrir y elegir),
          y el botón además muestra cuántas unidades trae, que es lo que convierte el precio en
          precio por unidad sin que nadie las teclee. */}
      {!sinCatalogo && (
        <>
          <p className="mb-1 mt-3 text-sm">
            Presentación <span className="text-xs text-gray-500">(opcional)</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {presentaciones.map((pr) => (
              <Opcion key={pr.id} centrado activo={fila.presentacionId === pr.id}
                onClick={() => {
                  setOtra(false);
                  // Se limpia el texto libre: las dos conviven en la fila y dejar el viejo escrito
                  // haría que el payload mandara una presentación del catálogo Y otra tecleada,
                  // contradiciéndose.
                  onCambio({ presentacionId: pr.id, presentacion: '' });
                }}>
                {pr.nombre}
                {pr.unidades !== null && (
                  <span className="block text-xs font-normal text-gray-500">
                    {pr.unidades} unid.
                  </span>
                )}
              </Opcion>
            ))}
            <Opcion centrado activo={otra}
              onClick={() => { setOtra(true); onCambio({ presentacionId: '' }); }}>
              Otra
            </Opcion>
          </div>
        </>
      )}

      {/* El texto libre NO desaparece: cubre lo que el catálogo no tiene, igual que `marcaLibre`
          cubre la marca que no está en la cuadrícula. Para una marca sin catálogo —hoy, toda
          toalla higiénica— es el único camino y por eso sale directo, sin pasar por «Otra». */}
      {escribiendo && (
        <label className="mt-2 flex flex-col text-sm">
          {sinCatalogo ? 'Presentación o tamaño (opcional)' : '¿Cuál?'}
          <input className="mt-1 min-h-[44px] rounded border px-3 text-base"
            placeholder="Ej.: XL x40, paquete chico"
            value={fila.presentacion}
            onChange={(e) => onCambio({ presentacion: e.target.value, presentacionId: '' })} />
        </label>
      )}

      {unidadesCatalogo !== null && fila.precioPor === 'paquete' && (
        // Se dice cuántas trae y cuál es la salida si no coinciden. La salida es «Otra» y no un
        // campo de unidades al lado: si el paquete que está mirando trae otra cantidad, entonces no
        // es esa presentación, y dejar el nombre del catálogo con un contenido distinto ensuciaría
        // justo la lista cerrada que se acaba de crear para poder contarlas.
        <p className="mt-2 text-xs text-gray-500">
          Esa presentación trae {unidadesCatalogo} unidades. Si el paquete que estás viendo trae
          otra cantidad, elige «Otra» y escríbela.
        </p>
      )}

      {pideUnidades && (
        <label className="mt-2 flex flex-col text-sm">
          ¿Cuántas unidades trae ese paquete?
          <input type="number" inputMode="numeric" min={1}
            className="mt-1 min-h-[44px] rounded border px-3 text-base"
            value={fila.unidades}
            onChange={(e) => onCambio({ unidades: e.target.value })} />
        </label>
      )}

      <CampoFotos
        etiqueta="Fotos del producto o su precio (opcional, puedes subir varias)"
        nombre={`las fotos de ${nombre.toLowerCase()}`}
        fotos={fila.fotos}
        onAgregar={onFotos}
        onQuitar={onQuitarFoto} />

      {/* Mostrarle el número calculado es más efectivo que un error: es con ese dato que él mismo
          detecta que anotó la unidad en vez del paquete. */}
      {ppu && <p className="mt-2 text-xs text-gray-600">{ppu}</p>}
      {rango && <p className="mt-1 text-xs text-amber-700">{rango}</p>}

      {/* El puesto que vende el mismo producto suelto Y por paquete necesita DOS filas de la misma
          marca. Acá cuesta un toque, con la marca ya puesta; sin esto había que agregar una fila
          libre y volver a teclear el nombre —o elegir cuál de los dos precios reportar y perder el
          otro, que es lo que hacía el modelo de un precio por marca—. */}
      {fila.marcaId !== '' && (
        <button type="button" onClick={onOtroPrecio}
          className="mt-3 min-h-[32px] text-xs text-blue-600 underline">
          + Otro precio de {nombre}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// La vista de avance del mercado (pedido del usuario, 2026-08-19)
// ---------------------------------------------------------------------------

/** El conteo, que es la mitad de lo que se pidió: *«cuántos puestos va y cuáles eran»*. */
export function textoAvance(cuantos: number): string {
  if (cuantos === 0) return 'Todavía no registraste ningún puesto en este mercado hoy';
  if (cuantos === 1) return 'Llevas 1 puesto en este mercado hoy';
  return `Llevas ${cuantos} puestos en este mercado hoy`;
}

/**
 * Cómo se le dice al auditor en qué punto está el envío de una ficha.
 *
 * Es la columna que decide si puede irse tranquilo del mercado: «en el celular» significa que el
 * dato existe SOLO en su teléfono, y que perderlo o formatearlo se lleva el trabajo del día.
 */
export function etiquetaEnvio(envio: EstadoEnvioVisita): string {
  if (envio === 'enviada') return 'Ya subió';
  if (envio === 'fallido') return 'No se pudo subir';
  return 'En el celular';
}

function claseEnvio(envio: EstadoEnvioVisita): string {
  if (envio === 'enviada') return 'text-green-700';
  if (envio === 'fallido') return 'text-red-700';
  return 'text-amber-700';
}

/** La hora de guardado en America/Lima, que es la única hora que significa algo en campo. */
export function horaDe(guardadaEn: number): string {
  return formatInTimeZone(new Date(guardadaEn), TZ, 'HH:mm');
}

/** Una línea de la lista de avance, venga del servidor o de la cola de este celular. */
export interface FilaAvance {
  clientUuid: string;
  numeroRecorrido: number;
  nombrePuesto: string | null;
  estado: EstadoVisita;
  /** Epoch ms: hora del servidor si ya subió, hora de guardado en el celular si no. */
  cuando: number;
  envio: EstadoEnvioVisita;
}

/**
 * Une lo que devolvió el SERVIDOR con lo que la cola de este celular todavía tiene, **sin
 * duplicar**.
 *
 * POR QUÉ DOS FUENTES Y NO UNA. Ninguna alcanza sola:
 *
 * - El servidor es la fuente de verdad y es la única que ve lo registrado desde otro teléfono o en
 *   una sesión anterior. Armar la lista solo con la cola local fue el error del 19-ago: el auditor
 *   entró desde otra máquina y la pantalla le dijo «todavía no registraste ningún puesto» teniendo
 *   22 visitas suyas cargadas.
 * - La cola local es la única que ve lo que TODAVÍA NO SUBIÓ, y adentro de un mercado sin señal
 *   eso es todo el trabajo del día. Si la lista fuera solo del servidor, cada puesto medido sin
 *   cobertura desaparecería del avance justo mientras se está midiendo.
 *
 * EL DEDUPE ES POR `client_uuid` y no por número de puesto: el número se repite de verdad —es el
 * del letrero— y dos puestos distintos con el mismo número quedarían colapsados en uno. El
 * `client_uuid` lo genera el celular al guardar y viaja hasta la fila del servidor, que es
 * idempotente por él; existe en las dos fuentes justamente para esto.
 *
 * GANA LA VERSIÓN DEL SERVIDOR. Es la que ya está a salvo y la única que sabe si el panel anuló la
 * visita después de subirla.
 *
 * NO se descartan las locales marcadas `enviada` que el servidor no trajo. Suena redundante y no lo
 * es: cuando la lectura del servidor falla —el caso que esta pantalla más va a vivir— la lista se
 * arma solo con lo local, y filtrarlas ahí borraría de la pantalla todo lo que este mismo celular
 * ya subió hoy. Si el servidor sí contestó, esas fichas vienen en su respuesta y el dedupe las
 * resuelve.
 */
export function unirAvance(
  servidor: readonly VisitaPropiaRow[], local: readonly ResumenVisitaLocal[],
): FilaAvance[] {
  const delServidor: FilaAvance[] = servidor.map((v) => ({
    clientUuid: v.clientUuid,
    numeroRecorrido: v.numeroRecorrido,
    nombrePuesto: v.nombrePuesto,
    estado: v.estado,
    // Una fecha que el navegador no sepa leer daría `NaN`, y un `NaN` en el criterio de orden
    // desordena la lista entera en vez de mover una fila. El 0 la manda al principio, que es
    // visible y no rompe nada.
    cuando: Number.isNaN(Date.parse(v.registradoEn)) ? 0 : Date.parse(v.registradoEn),
    envio: 'enviada',
  }));
  const yaEstan = new Set(delServidor.map((v) => v.clientUuid));
  const soloLocales: FilaAvance[] = local
    .filter((v) => !yaEstan.has(v.clientUuid))
    .map((v) => ({
      clientUuid: v.clientUuid,
      numeroRecorrido: v.numeroRecorrido,
      nombrePuesto: v.nombrePuesto,
      estado: v.estado,
      cuando: v.guardadaEn,
      envio: v.envio,
    }));
  // El `clientUuid` desempata para que dos fichas de la misma hora no bailen entre renders.
  return [...delServidor, ...soloLocales]
    .sort((a, b) => a.cuando - b.cuando || a.clientUuid.localeCompare(b.clientUuid));
}

/** Los números ya usados hoy en este mercado, para la bandera de repetido. */
export function numerosDeAvance(visitas: readonly FilaAvance[]): number[] {
  return visitas.map((v) => v.numeroRecorrido);
}

/**
 * Lo que esta cuenta lleva registrado del mercado hoy: lo que ya subió al sistema **y** lo que
 * sigue esperando señal en este celular.
 *
 * **PLEGADO POR DEFECTO, con el conteo en la cabecera.** Es lo que resuelve las dos mitades del
 * pedido sin pelearse entre ellas: el número —«llevas 7 puestos»— se ve siempre, y la lista de
 * cuáles fueron no empuja el formulario media pantalla hacia abajo en cada ficha.
 *
 * EL AVISO DE «no se pudo consultar el servidor» VA FUERA DEL PLEGADO. Adentro solo lo vería quien
 * abre la lista, y es justo el dato que cambia cómo se lee el conteo de la cabecera: sin servidor
 * ese número es lo de este teléfono, no lo de la cuenta.
 */
function BloqueAvance(
  { visitas, abierto, servidorCaido, onAlternar }:
  {
    visitas: FilaAvance[]; abierto: boolean; servidorCaido: boolean; onAlternar: () => void;
  },
) {
  return (
    <section className="mb-5 rounded-lg border bg-white">
      <button type="button" onClick={onAlternar} aria-expanded={abierto}
        className="flex min-h-[48px] w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="text-sm font-semibold text-gray-800">{textoAvance(visitas.length)}</span>
        <span className="shrink-0 text-xs text-blue-700 underline">
          {abierto ? 'Ocultar' : 'Ver cuáles'}
        </span>
      </button>

      {servidorCaido && (
        <p className="mx-4 mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          No se pudo consultar el servidor; esto es lo que hay en este celular. Puede faltar lo que
          registraste desde otro equipo.
        </p>
      )}

      {abierto && (
        <div className="border-t px-4 py-3">
          {visitas.length === 0 ? (
            <p className="text-sm text-gray-600">
              Acá van apareciendo los puestos a medida que los vas guardando.
            </p>
          ) : (
            <ul className="grid gap-2">
              {visitas.map((v) => (
                <li key={v.clientUuid}
                  className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-gray-100 pb-2 text-sm last:border-b-0 last:pb-0">
                  <span className="font-medium">{tituloDePuesto(v)}</span>
                  <span className="text-xs text-gray-600">
                    {horaDe(v.cuando)} · {etiquetaEstadoVisita(v.estado)}
                  </span>
                  <span className={`text-xs font-medium ${claseEnvio(v.envio)}`}>
                    {etiquetaEnvio(v.envio)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* La nota anterior decía que esta lista era «la de ESTE celular» y que lo subido se
              borraba de acá a los 7 días. Dejó de ser cierto: ahora la lista sale del servidor y
              la cola local solo agrega lo que aún no subió, así que ni depende del teléfono ni se
              vacía con la purga. Lo que SÍ sigue siendo un límite real —y el que importa en
              campo— es que la cuenta de auditor es genérica y compartida: dos personas pueden
              estar midiendo el mismo mercado con cuentas distintas. */}
          <p className="mt-3 border-t pt-2 text-xs text-gray-500">
            Acá sale lo que registraste con ESTA cuenta en este mercado hoy, hayas usado este
            celular u otro. Si alguien más midió el mismo mercado con otra cuenta, sus puestos no
            aparecen acá.
          </p>
        </div>
      )}
    </section>
  );
}

const nuevoId = (): string => crypto.randomUUID();

/**
 * Ficha de puesto del levantamiento de canal, **armada por CATEGORÍA de producto**.
 *
 * **Una respuesta por PUESTO dentro del mercado.** El auditor llega, elige el mercado una vez y
 * a partir de ahí llena una ficha por cada puesto que logre medir — pueden ser treinta. Al
 * terminar cierra la visita con el cuestionario aparte.
 *
 * ## Qué cambió y por qué (2026-08-20)
 *
 * El pedido del usuario fue *«que el formulario sea lo más fácil para el recurso de subirlo […]
 * tener esos campos separados por categoría de producto»*. Lo que había preguntaba **las 6 marcas a
 * todos los puestos**, y contra producción eso significaba que al 29% de los puestos medidos —los
 * que declararon vender solo papel, o abarrotes— se les preguntó igual por Babysec y Huggies. Lo
 * que salía de ahí era un «no lo trabaja» que mezcla al puesto que decidió no tener la marca con el
 * que ni siquiera está en ese negocio: dos hechos opuestos, uno es una oportunidad comercial y el
 * otro no, y fundirlos es de lo poco que después no se puede separar.
 *
 * Ahora la ficha pregunta primero **qué categorías vende** (varias, casillas) y con eso recorta todo
 * lo demás: la cuadrícula muestra solo las marcas de esas categorías, y los precios solo las marcas
 * que contestó «Tiene».
 *
 * ## La cuenta de TOQUES, que es lo que decide si esto sirve
 *
 * Un puesto que vende solo papel higiénico, con la configuración de hoy (3 marcas de papel de 6):
 *
 * | | antes | ahora |
 * |---|---|---|
 * | número del puesto | 1 | 1 |
 * | qué vende | 1 (`giro`, opción única) | 1 (casilla «Papel higiénico») |
 * | a quién le vende | 1 | 1 |
 * | cuadrícula | **6** (las 6 marcas) | **3** (solo las de papel) |
 * | promoción / recomendación / motivo | 3 | 3 |
 * | foto + guardar | 2 | 2 |
 * | **total** | **14** | **11** |
 *
 * Y el margen crece con el catálogo: el día que las marcas sean 13 en vez de 6, ese mismo puesto
 * sigue costando 11 toques y el formulario viejo costaría 21. Los campos nuevos NO agregan toques
 * al caso frecuente porque nacen resueltos o plegados: `precio_por` nace en «paquete» (que es lo
 * que la columna ya significaba), el contacto y las observaciones empiezan como un enlace, y la
 * presentación pasó de teclearse a ser un botón — un toque en vez de escribir «XL x 45 metros».
 *
 * **No es un asistente de 4 pantallas.** Se evaluó y se descartó: tres botones de «Siguiente» son
 * tres toques más por puesto —noventa en un recorrido de treinta— a cambio de nada que el recorte
 * por categorías no dé ya. Las secciones aparecen a medida que se contestan, que es la misma guía
 * sin el peaje.
 *
 * Todo funciona **sin señal**: dentro de los mercados no hay cobertura, y eso no es un caso
 * raro sino la condición normal de trabajo. Por eso guardar es ENCOLAR (ver `guardar`), por eso el
 * filtro por categorías corre en memoria —las marcas y sus presentaciones se piden UNA vez al
 * elegir la campaña, nunca por tilde— y por eso la vista de avance suma a lo que devuelve el
 * servidor lo que la cola de este celular todavía no pudo subir.
 */
export default function FichaPuesto() {
  const fecha = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
  /**
   * Los roles deciden si el banco de pruebas aparece en el desplegable de campaña.
   *
   * Al auditor —rol `recurso` y nada más— NO se le muestra: el 19-ago eligió la campaña demo del
   * desplegable y ahí quedaron 18 puestos de trabajo real. Al de oficina sí, rotulada, porque el
   * banco existe para ensayar el formulario y para mostrárselo a un cliente.
   */
  const { roles, loading: cargandoSesion } = useAuth();
  /** Clave estable de los roles: `roles` es un arreglo nuevo en cada render y como dependencia de
   *  un efecto lo dispararía sin parar. */
  const rolesKey = roles.join(',');
  const [campanias, setCampanias] = useState<CampaniaLevantamiento[]>([]);
  const [campaignId, setCampaignId] = useState('');
  const [mercados, setMercados] = useState<MercadoLevantamiento[]>([]);
  /** El mercado elegido al llegar. Se conserva puesto tras puesto: se pregunta UNA vez.
   *  `MERCADO_OTRO` = no está en la lista y se escribe en `mercadoLibre`. */
  const [pdvId, setPdvId] = useState('');
  const [mercadoLibre, setMercadoLibre] = useState('');
  const [marcas, setMarcas] = useState<MarcaLevantamiento[]>([]);
  /**
   * El catálogo de presentaciones de TODAS las marcas de la campaña, traído una sola vez.
   *
   * Se pide junto con las marcas y no cuando la auditora abre una fila de precio: adentro de un
   * mercado no hay señal, así que una consulta en ese momento devolvería la lista vacía justo
   * cuando hace falta, y la presentación volvería a ser texto libre —que es el problema que este
   * catálogo viene a resolver (nueve escrituras distintas para dos presentaciones)—.
   */
  const [presentaciones, setPresentaciones] = useState<PresentacionMarca[]>([]);
  const [b, setB] = useState<BorradorFicha | null>(null);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [pendientes, setPendientes] = useState(0);
  /**
   * Fichas que la cola dio por perdidas. **Se cuentan aparte de las pendientes porque no son lo
   * mismo**: una pendiente espera señal y sube sola; una fallida ya agotó sus cinco intentos y
   * `flushVisitas` no la vuelve a mirar nunca. Sin este contador, el trabajo de un puesto medido se
   * queda en el celular y nadie se entera — que fue exactamente lo que pasó: 46 fichas quedaron así
   * entre el 19 y el 22 de agosto, con las fotos ya subidas y sin ninguna fila en la base.
   */
  const [fallidas, setFallidas] = useState(0);
  /**
   * Cuántas fichas se guardaron en esta sesión. **Es la clave de remontaje del formulario.**
   *
   * Sin esto, los campos que se despliegan solos (`Plegable`: la observación de cada sección, el
   * contacto del puestero) guardan su «abierto» en estado propio, que `setB(borradorVacio(…))` no
   * toca: el puesto siguiente arrancaba con tres cajas de texto abiertas y vacías, heredadas del
   * anterior. Cambiar la clave desmonta y vuelve a montar el formulario, que es exactamente lo que
   * «empezar otra ficha» significa.
   */
  const [fichaSeq, setFichaSeq] = useState(0);
  /**
   * Las DOS fuentes del avance, guardadas por separado y unidas al pintar.
   *
   * Separadas y no en un solo `useState` con la lista ya unida porque se refrescan en momentos
   * distintos: lo local después de CADA guardado (una lectura de IndexedDB, instantánea) y lo del
   * servidor solo cuando puede haber señal. Si compartieran estado, la relectura local que corre
   * al guardar un puesto pisaría lo que trajo el servidor y la lista perdería, en cada ficha, todo
   * lo registrado desde otro equipo.
   */
  const [avanceLocal, setAvanceLocal] = useState<ResumenVisitaLocal[]>([]);
  const [avanceServidor, setAvanceServidor] = useState<VisitaPropiaRow[]>([]);
  /** La lectura del servidor falló (sin señal, típicamente). Se DICE en la pantalla. */
  const [servidorCaido, setServidorCaido] = useState(false);
  /** Plegado por defecto: el formulario es lo que la persona vino a llenar. */
  const [avanceAbierto, setAvanceAbierto] = useState(false);
  const inicioRef = useRef<HTMLDivElement | null>(null);
  /** Número de la última consulta al servidor. Ver `recargarServidor`: descarta las tardías. */
  const peticionServidor = useRef(0);

  // Se ESPERA a que la sesión termine de cargar antes de pedir las campañas. Sin roles todavía,
  // `campaniasParaElegir` trata a la cuenta como de campo y escondería el banco de pruebas incluso
  // al de oficina: la lista quedaría fija y equivocada, porque este efecto no volvería a correr.
  useEffect(() => {
    if (cargandoSesion) return;
    let vivo = true;
    (async () => {
      try {
        const cs = await listCampaniasLevantamiento(roles);
        if (!vivo) return;
        setCampanias(cs);
        if (cs.length === 1) setCampaignId(cs[0].id);
        setPendientes(await pendingVisitaCount());
        setFallidas((await listFailedVisitas()).length);
      } catch (e) {
        if (vivo) setError('No se pudo cargar: ' + (e instanceof Error ? e.message : String(e)));
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
    // `rolesKey` y no `roles`: ver arriba. `roles` se usa adentro y cambia junto con la clave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargandoSesion, rolesKey]);

  useEffect(() => {
    if (campaignId === '') { setMercados([]); setMarcas([]); setPresentaciones([]); return; }
    let vivo = true;
    (async () => {
      try {
        // Sin `categorias`: se traen TODAS las marcas de la campaña y el recorte por categoría lo
        // hace `marcasDeCategorias` en memoria. Filtrar en el servidor obligaría a una consulta por
        // cada casilla que la auditora tilda, adentro de un mercado sin cobertura.
        const [mk, mc] = await Promise.all([
          listMarcasLevantamiento(campaignId),
          listMercadosLevantamiento(campaignId),
        ]);
        if (!vivo) return;
        setMarcas(mk);
        setMercados(mc);
        // El catálogo de presentaciones va APARTE y con su propio `catch`, y es a propósito: es lo
        // único de esta carga que la ficha puede no tener y seguir sirviendo —sin él la
        // presentación vuelve a ser el campo de texto de siempre—. Metido en el `Promise.all`, un
        // fallo suyo se llevaría también las marcas y los mercados, y sin marcas la ficha ni
        // siquiera arranca.
        try {
          const ps = await listPresentacionesDeMarcas(mk.map((m) => m.id));
          if (vivo) setPresentaciones(ps);
        } catch { if (vivo) setPresentaciones([]); }
      } catch (e) {
        if (vivo) setError('No se pudo cargar la campaña: ' + (e instanceof Error ? e.message : String(e)));
      }
    })();
    return () => { vivo = false; };
  }, [campaignId]);

  /** Vacío hasta que el mercado esté definido: de la lista, o escrito a mano. */
  const claveMercado = pdvId === '' ? ''
    : pdvId === MERCADO_OTRO
      ? (mercadoLibre.trim() === '' ? '' : claveDeMercado('', mercadoLibre))
      : claveDeMercado(pdvId, '');

  /**
   * Al elegir el mercado arranca la ficha del primer puesto **con el número VACÍO**.
   *
   * Antes se precargaba `ultimo + 1` con `ultimoRecorridoLocal`, porque se creía que el campo
   * era el correlativo del recorrido. El 19-ago el usuario confirmó que NO: es el número REAL
   * del puesto dentro del mercado —el del letrero, el de su ubicación física—. Autoincrementarlo
   * precargaba un número casi siempre equivocado que el auditor tenía que sobrescribir puesto
   * por puesto; se comprobó contra producción: ese mismo día en Mercado Naranjal se registraron
   * 23, 26, 3, 11 y 16 en ese orden de reloj. **Basta que se le olvide UNA vez para que quede
   * registrado un puesto que no es**, y un número inventado no se distingue de uno real al leer
   * la base.
   *
   * Efecto colateral que conviene dejar dicho: `ultimoRecorridoLocal` (`visitaQueue.ts`) queda
   * sin ningún llamador fuera de su prueba. La lista completa del día —la que alimenta el aviso
   * de repetido y la vista de avance— la da `visitasLocalesDe`, sobre la que esa función se
   * apoya; el máximo del día ya no lo necesita nadie.
   */
  useEffect(() => {
    setB(claveMercado === '' || marcas.length === 0 ? null : borradorVacio(marcas));
  }, [claveMercado, marcas]);

  /**
   * Relee del celular lo registrado en este mercado hoy. Es una lectura de IndexedDB: no toca la
   * red, no puede tardar y no puede fallar por falta de señal.
   *
   * Se llama al elegir el mercado y **después de CADA guardado**: es lo que hace que la lista y el
   * conteo estén al día sin recargar la pantalla, y lo que mantiene la bandera de repetido mirando
   * el estado real de la base local en vez de una copia que la pantalla vaya arrastrando.
   */
  const recargarLocal = useCallback(async (): Promise<ResumenVisitaLocal[]> => {
    if (claveMercado === '') { setAvanceLocal([]); return []; }
    const lista = await visitasLocalesDe(claveMercado, fecha);
    setAvanceLocal(lista);
    return lista;
  }, [claveMercado, fecha]);

  /**
   * Pregunta al SERVIDOR qué registró esta cuenta hoy en este mercado.
   *
   * **NUNCA LANZA NI TOCA EL FORMULARIO.** Un fallo acá es lo normal adentro de un mercado, y
   * perder el aviso de avance no puede impedir seguir midiendo: no escribe en `error` —que es el
   * cartel rojo del formulario— sino en `servidorCaido`, que solo cambia lo que dice el bloque de
   * avance. Un `setError` acá le pintaría al auditor «no se pudo guardar» al lado de una ficha que
   * se guardó perfecto.
   */
  const recargarServidor = useCallback(async (): Promise<void> => {
    // El contador descarta las respuestas que llegan TARDE. No es ceremonia: esta lectura se
    // dispara al elegir el mercado y otra vez después de cada puesto guardado, y sin señal tarda
    // hasta agotar su espera. Si en ese rato se cambia de mercado, la respuesta del anterior
    // llegaría después y pintaría sus puestos como si fueran los de este — y con ellos la bandera
    // de «este número ya lo usaste», que le haría dudar de un número que nunca usó acá.
    const mia = peticionServidor.current + 1;
    peticionServidor.current = mia;
    if (claveMercado === '' || campaignId === '') {
      setAvanceServidor([]); setServidorCaido(false); return;
    }
    try {
      const lista = await listMisVisitas({
        campaignId, desde: fecha, hasta: fecha, claveMercado,
      });
      if (mia !== peticionServidor.current) return;
      setAvanceServidor(lista);
      setServidorCaido(false);
    } catch {
      if (mia !== peticionServidor.current) return;
      // Se conserva lo que ya se había traído: si el servidor contestó al llegar al mercado y
      // después se cayó la señal, borrar esa lista haría desaparecer de la pantalla puestos que
      // el auditor SÍ tiene registrados. Lo que cambia es que ahora se avisa que está vieja.
      setServidorCaido(true);
    }
  }, [claveMercado, fecha, campaignId]);

  useEffect(() => {
    // Cambiar de mercado empieza otra cuenta: los puestos de un mercado no dicen nada del otro.
    setAvanceLocal([]);
    setAvanceServidor([]);
    setServidorCaido(false);
    if (claveMercado === '') return undefined;
    let vivo = true;
    // El `vivo` cumple para lo local lo mismo que el contador para lo del servidor: descartar la
    // respuesta del mercado anterior si se cambió mientras la lectura estaba en vuelo.
    void visitasLocalesDe(claveMercado, fecha).then((lista) => { if (vivo) setAvanceLocal(lista); });
    void recargarServidor();
    return () => { vivo = false; };
  }, [claveMercado, fecha, recargarServidor]);

  /**
   * La lista que ve el auditor: el servidor manda y la cola local agrega lo que aún no subió.
   *
   * Alimenta DOS cosas —el bloque de avance y la bandera de «este número ya lo usaste»— y por eso
   * es una sola: si la bandera mirara otra lista, avisaría de repetidos distintos de los que la
   * pantalla muestra, y el auditor no tendría cómo entender el aviso.
   */
  const avance = useMemo(
    () => unirAvance(avanceServidor, avanceLocal), [avanceServidor, avanceLocal],
  );

  /** Los números ya usados hoy en este mercado por esta cuenta, subidos o no. */
  const usados = useMemo(() => numerosDeAvance(avance), [avance]);

  /**
   * **EL RECORTE, y el centro entero del rediseño**: las marcas que le tocan a ESTE puesto.
   *
   * Todo lo que se pregunta cuelga de acá —la cuadrícula, las filas de precio que se proponen, lo
   * que se valida al guardar y lo que finalmente viaja—. Con categorías vacías devuelve la lista
   * vacía, así que la ficha no muestra ninguna marca hasta que la auditora dice qué vende el
   * puesto: es la diferencia entre «todavía no contestó» y «vende de todo».
   */
  const marcasVisibles = useMemo(
    () => (b === null ? [] : marcasDeCategorias(marcas, b.categorias)),
    [marcas, b?.categorias],
  );

  const proponenPrecio = useMemo(
    () => (b === null ? [] : marcasQueProponenPrecio(marcasVisibles, b.presencias)),
    [marcasVisibles, b?.presencias],
  );

  /**
   * El atajo «lo mismo que el puesto anterior». Sale de la COLA de este celular y no del servidor:
   * es el recorrido de ESTA persona en ESTE mercado hoy, y tiene que funcionar sin señal — que es
   * la condición de todo lo que la ficha ofrece.
   *
   * Va acá arriba, con los otros `useMemo`, y no al lado de donde se usa: más abajo la pantalla ya
   * tiene returns condicionales (`cargando`, «no hay campañas»), y un hook después de uno de ellos
   * se salta en unos renders y en otros no. React lo dice sin rodeos —«rendered more hooks than
   * during the previous render»— y se descubrió justo así.
   */
  const categoriasAnteriores = useMemo(
    () => categoriasDelPuestoAnterior(avanceLocal), [avanceLocal],
  );

  /**
   * Cuando una marca pasa a «Tiene», se le PROPONE su fila de precio; si deja de tenerla —o si la
   * categoría entera se destilda—, la fila propuesta y todavía vacía se retira sola.
   *
   * Es propuesta, no candado: el precio es opcional según el Excel, y el auditor puede borrarla o
   * agregar las que quiera. Y es lo que hace que el paso 3 se limite solo: si el puesto tiene 5 de
   * 13 marcas, aparecen 5 filas de precio y no 13.
   *
   * Todo se calcula DENTRO de `setB((prev) => …)` y devolviendo `prev` sin tocar cuando no hay nada
   * que hacer. Las dos cosas importan: leer `b.precios` del render sería leer una copia vieja
   * mientras una compresión de foto está en vuelo, y devolver un objeto nuevo cada vez volvería a
   * disparar este efecto en bucle.
   */
  useEffect(() => {
    const quiere = new Set(proponenPrecio.map((m) => m.id));
    setB((prev) => {
      if (!prev) return prev;
      const tiene = new Set(prev.precios.filter((p) => p.marcaId !== '').map((p) => p.marcaId));
      const faltan = [...quiere].filter((id) => !tiene.has(id));
      const sobran = prev.precios.filter(
        (p) => p.marcaId !== '' && !quiere.has(p.marcaId) && filaDePrecioSinDatos(p),
      );
      if (faltan.length === 0 && sobran.length === 0) return prev;
      return {
        ...prev,
        precios: [
          ...prev.precios.filter((p) => !sobran.includes(p)),
          ...faltan.map((id) => filaPrecioVacia(nuevoId(), id)),
        ],
      };
    });
  }, [proponenPrecio]);

  function set<K extends keyof BorradorFicha>(campo: K, valor: BorradorFicha[K]) {
    setB((prev) => (prev ? { ...prev, [campo]: valor } : prev));
    setError('');
  }

  function alternar(c: CategoriaProducto) {
    setB((prev) => (prev ? { ...prev, categorias: alternarCategoria(prev.categorias, c) } : prev));
    setError('');
  }

  function setPresencia(marcaId: string, p: Presencia) {
    setB((prev) => (prev ? { ...prev, presencias: { ...prev.presencias, [marcaId]: p } } : prev));
    setError('');
  }

  function setObs(seccion: SeccionObservable, v: string) {
    setB((prev) => (prev
      ? { ...prev, observaciones: { ...prev.observaciones, [seccion]: v } } : prev));
    setError('');
  }

  function setPrecio(id: string, cambio: Partial<FilaPrecio>) {
    setB((prev) => (prev
      ? { ...prev, precios: prev.precios.map((p) => (p.id === id ? { ...p, ...cambio } : p)) }
      : prev));
    setError('');
  }

  function setPromo(id: string, cambio: Partial<FilaPromocion>) {
    setB((prev) => (prev
      ? { ...prev, promociones: prev.promociones.map((p) => (p.id === id ? { ...p, ...cambio } : p)) }
      : prev));
    setError('');
  }

  // -------------------------------------------------------------------------
  // Fotos: los cuatro campos aceptan varias y todas se manipulan igual
  // -------------------------------------------------------------------------

  /**
   * Los cuatro destinos posibles de una lista de fotos.
   *
   * Todos actualizan con la FORMA FUNCIONAL (`setB((prev) => …)`) y no leyendo `b` del render:
   * comprimir una foto tarda, y con dos elecciones seguidas —o una elección y un «quitar»
   * mientras la primera todavía comprime— la segunda escritura pisaría a la primera con una copia
   * vieja de la ficha, y las fotos recién agregadas desaparecerían.
   */
  const enFotoPuesto: DestinoFotos = (mezcla) =>
    setB((prev) => (prev ? { ...prev, fotoPuesto: mezcla(prev.fotoPuesto) } : prev));

  const enFotoHallazgo: DestinoFotos = (mezcla) =>
    setB((prev) => (prev ? { ...prev, fotoHallazgo: mezcla(prev.fotoHallazgo) } : prev));

  const enFotosPrecio = (id: string): DestinoFotos => (mezcla) =>
    setB((prev) => (prev
      ? {
        ...prev,
        precios: prev.precios.map((p) => (p.id === id ? { ...p, fotos: mezcla(p.fotos) } : p)),
      }
      : prev));

  const enFotosPromo = (id: string): DestinoFotos => (mezcla) =>
    setB((prev) => (prev
      ? {
        ...prev,
        promociones: prev.promociones.map(
          (p) => (p.id === id ? { ...p, fotos: mezcla(p.fotos) } : p),
        ),
      }
      : prev));

  /** Comprime lo recién elegido y lo SUMA al final: elegir tres y luego una deja cuatro. */
  function sumarFotos(files: File[], destino: DestinoFotos): void {
    if (files.length === 0) return;
    void comprimirTodas(files)
      .then((nuevas) => { if (nuevas.length > 0) destino((ya) => [...ya, ...nuevas]); })
      .catch((e) => setError(
        'No se pudo preparar la foto: ' + (e instanceof Error ? e.message : String(e)),
      ));
  }

  /**
   * Guarda la ficha: **la ENCOLA siempre y recién después intenta subirla** (2026-08-19).
   *
   * ANTES ERA AL REVÉS —subía directo y solo encolaba si algo fallaba— y eso tenía tres defectos,
   * los tres invisibles mientras hubiera señal:
   *
   * 1. **La ficha viajaba antes de estar a salvo.** Entre el primer `upload` y la RPC hay varios
   *    segundos con fotos de por medio; si en ese rato se cerraba la pestaña, se acababa la
   *    batería o el navegador mataba la app por memoria, lo llenado se perdía entero y no quedaba
   *    ninguna copia. Encolar primero es una escritura local, rápida y que no depende de la red.
   * 2. **Las fichas guardadas CON señal nunca pasaban por el celular**, así que la vista de avance
   *    —que se arma con lo que hay en la base local— salía vacía justo cuando había cobertura, que
   *    es cuando el auditor la mira para decidir si ya puede irse del mercado.
   * 3. **Eran dos códigos distintos** para lo mismo: el camino con señal subía las fotos acá y el
   *    camino sin señal las subía en `flushVisitas`. Dos implementaciones del mismo envío divergen
   *    —ya divergieron: el índice de la foto se agregó en una y no en la otra— y la que se rompe es
   *    siempre la que casi nadie ejecuta.
   *
   * De yapa, `flushVisitas` corre en CADA guardado, que es la cadencia correcta para la purga de
   * enviadas viejas que vive adentro: se limpia mientras se trabaja, no en un temporizador que en
   * un celular dormido no dispara nunca.
   *
   * **Nunca se pierde una ficha por no tener señal**: encolar es el camino normal, no el de
   * excepción. El GPS se pide en el mismo gesto (iOS solo entrega el permiso desde un toque) y
   * si no llega se registra igual con la bandera `sin_gps`.
   */
  async function guardar() {
    if (!b || claveMercado === '' || campaignId === '') return;
    // ⚠️ LAS TRES SALIDAS MIRAN EL MISMO BORRADOR RECORTADO. Validar sobre el completo y guardar
    // sobre el recortado —o al revés— dejaría a la pantalla exigiendo una respuesta que después no
    // viaja, o guardando una medición de una categoría que la ficha dice que el puesto no vende.
    const efectivo = borradorEfectivo(b, marcasVisibles);
    const falta = loQueFaltaEnFicha(efectivo, marcasVisibles, presentaciones);
    if (falta !== null) {
      setError(falta);
      inicioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setEnviando(true);
    setError('');
    try {
      const { fix } = await getGpsFix();
      const clientUuid = crypto.randomUUID();
      const input = {
        ...visitaDesdeBorrador(efectivo, marcasVisibles),
        clientUuid,
        campaignId,
        pdvId: pdvId === MERCADO_OTRO ? '' : pdvId,
        mercadoLibre: pdvId === MERCADO_OTRO ? mercadoLibre.trim() : '',
        // Sin programación no hay asignación que enganchar, y es lo esperado en este módulo:
        // el mercado lo eligió la persona.
        assignmentId: null,
        fecha,
        lat: fix?.lat ?? null,
        lng: fix?.lng ?? null,
        accuracy: fix?.accuracy ?? null,
      };
      // Del RECORTADO también: si una fila de precio no viaja, sus fotos tampoco tienen que
      // subirse. Con el borrador completo quedarían archivos en el bucket bajo una clave que
      // ningún payload menciona, y nadie se enteraría porque nada falla.
      const fotos = fotosDelBorrador(efectivo);

      // PASO 1, y el único que no puede fallar: la ficha queda escrita en el celular.
      await enqueueVisita({ clientUuid, input, fotos });

      // PASO 2: se intenta subir todo lo que esté esperando, esta ficha incluida. Un fallo acá NO
      // es un error del guardado —estar sin señal adentro de un mercado es la condición normal—,
      // así que se traga: la ficha ya está a salvo y la cola la reintentará sola.
      try {
        await flushVisitas();
      } catch { /* sin señal o sin enviador: queda en la cola, que es exactamente su trabajo */ }

      // Se relee la base local en vez de mirar el `sent` de `flushVisitas`, y no es lo mismo: ese
      // número cuenta TODAS las fichas que subieron en esa pasada, así que con dos atrasadas de
      // ayer diría «subió» aunque la de este puesto se hubiera quedado. Acá se pregunta por ESTA.
      const lista = await recargarLocal();
      const subida = lista.find((v) => v.clientUuid === clientUuid)?.envio === 'enviada';

      // El servidor se vuelve a preguntar SIN ESPERARLO. Es a propósito: guardar un puesto no
      // puede quedarse colgado de una consulta que adentro del mercado va a agotar su tiempo de
      // espera, y el auditor necesita el aviso de «guardado» ya para pasar al siguiente. Lo que
      // traiga entra en la lista cuando llegue; si no llega, el bloque de avance lo dice.
      void recargarServidor();

      // Siguiente puesto: se limpia todo menos quién mide. El número TAMBIÉN se limpia —antes
      // avanzaba solo— porque es el número real del puesto en el mercado y no el orden del
      // recorrido: precargarlo proponía un valor casi siempre falso, y el descuido de no
      // corregirlo dejaba registrado un puesto que no era. Escribirlo cuesta dos toques; un
      // número equivocado no se detecta nunca al leer la base. El nombre del puesto se limpia por
      // lo mismo: es de ESE puesto y arrastrarlo etiquetaría mal al siguiente.
      //
      // LAS CATEGORÍAS TAMBIÉN SE LIMPIAN, aunque arrastrarlas ahorraría un toque por puesto: qué
      // vende es un hecho de ESE puesto —el de al lado vende otra cosa— y heredarlo es el mismo
      // accidente que el número autoincrementado, con el agravante de que acá no se nota: la
      // cuadrícula aparecería llena de las marcas del puesto anterior y nada se vería raro.
      setB({ ...borradorVacio(marcas), auditorDeclarado: b.auditorDeclarado });
      setFichaSeq((n) => n + 1);
      setPendientes(await pendingVisitaCount());
      setFallidas((await listFailedVisitas()).length);
      // Los dos mensajes siguen siendo distintos a propósito: «quedó en el celular» es lo que le
      // dice al auditor que ese dato todavía existe en un solo lugar y que no puede irse del
      // mercado dándolo por entregado.
      setMensaje(
        subida
          ? `Puesto ${input.numeroRecorrido} guardado y subido. Escribe el número del siguiente.`
          : `Puesto ${input.numeroRecorrido} guardado en el celular (sin señal). Se sube solo apenas haya. Escribe el número del siguiente.`,
      );
      inicioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      setError('No se pudo guardar: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setEnviando(false);
    }
  }

  async function subirPendientes() {
    setEnviando(true);
    try {
      const { sent, remaining } = await flushVisitas();
      setPendientes(remaining);
      setFallidas((await listFailedVisitas()).length);
      // La lista de avance cambia con esto: lo que estaba «en el celular» pasa a «ya subió», que
      // es justo el dato que el auditor vino a mirar cuando tocó este botón. Acá SÍ se espera al
      // servidor —al revés que al guardar un puesto—: este botón es un pedido explícito de hablar
      // con la red, no hay una ficha a medio llenar esperando, y es el momento en que conviene
      // traer también lo que se registró desde otro equipo.
      await recargarLocal();
      await recargarServidor();
      setMensaje(sent > 0 ? `${sent} ficha(s) subidas.` : 'Todavía no hay señal para subirlas.');
    } catch (e) {
      setError('No se pudieron subir: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setEnviando(false);
    }
  }

  /**
   * El rescate de las fichas dadas por perdidas: vuelven a la cola y se intenta subirlas ya.
   *
   * Es un botón y no algo automático a propósito. Una ficha llega a `fallido` porque el servidor la
   * RECHAZÓ por algo definitivo, y reintentarla sola en cada arranque sería golpear una puerta
   * cerrada gastando batería y datos. Cuando la causa se arregla del lado del servidor —como el
   * `marca_id` NOT NULL que la `0060` acaba de quitar— alguien tiene que decir «probá de nuevo», y
   * ese alguien es quien tiene el celular con el dato adentro.
   */
  async function rescatarFallidas() {
    setEnviando(true);
    try {
      const cuantas = await reencolarVisitasFallidas();
      setFallidas(0);
      const { sent, remaining } = await flushVisitas();
      setPendientes(remaining);
      setFallidas((await listFailedVisitas()).length);
      await recargarLocal();
      await recargarServidor();
      setMensaje(sent > 0
        ? `${sent} de ${cuantas} ficha(s) atascadas se subieron.`
        : 'Se volvieron a poner en cola, pero todavía no se pudieron subir.');
    } catch (e) {
      setError('No se pudieron rescatar: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setEnviando(false);
    }
  }

  if (cargando) return <p className="p-4 text-gray-500">Cargando…</p>;

  if (campanias.length === 0) {
    return (
      <div className="p-4">
        <h1 className="mb-2 text-lg font-semibold">Ficha de puesto</h1>
        <p className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          No hay ninguna campaña de levantamiento activa para ti. Avísale a tu coordinador.
        </p>
      </div>
    );
  }

  const medido = b !== null && b.estado === 'medido';
  const numeroRepetido = b !== null && numeroYaUsado(b.numeroRecorrido, usados);
  const mercado = mercados.find((m) => m.id === pdvId);
  const nombreMercado = pdvId === MERCADO_OTRO ? mercadoLibre.trim() : mercado?.nombre;
  /** Ya dijo qué vende: recién ahí tiene sentido preguntarle marca por marca. */
  const declaroCategorias = b !== null && b.categorias.length > 0;
  const mismasCategoriasQueElAnterior = b !== null
    && categoriasAnteriores.length === b.categorias.length
    && categoriasAnteriores.every((c) => b.categorias.includes(c));
  const copiarDelAnterior = () => {
    setB((prev) => (prev ? { ...prev, categorias: [...categoriasAnteriores] } : prev));
  };
  const huerfanas = b === null ? [] : categoriasNoDeclaradasConDatos(b, marcas);
  const grupos = b === null ? [] : gruposDePrecio(b.precios, marcas, b.categorias);

  return (
    <div className="p-4 pb-28">
      <div ref={inicioRef} />
      <h1 className="text-lg font-semibold">Ficha de puesto</h1>
      <p className="mb-3 text-sm text-gray-600">
        {formatInTimeZone(new Date(), TZ, 'dd/MM/yyyy')}
        {nombreMercado && ` · ${nombreMercado}`}
      </p>

      {pendientes > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span>{pendientes} ficha(s) guardadas en el celular, sin subir.</span>
          <button type="button" onClick={() => void subirPendientes()} disabled={enviando}
            className="rounded bg-amber-600 px-3 py-1 text-white disabled:opacity-50">
            Intentar subirlas
          </button>
        </div>
      )}

      {/* Las fallidas van en ROJO y aparte de las pendientes: una pendiente sube sola en cuanto
          haya señal, una fallida NO se reintenta nunca sin que alguien lo pida. Fundirlas en el
          mismo aviso amarillo le diría al auditor que espere algo que no va a pasar. */}
      {fallidas > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
          <span>
            <strong>{fallidas} ficha(s) no se pudieron subir</strong> y quedaron atascadas en este
            celular. El dato todavía está acá: no borres la aplicación.
          </span>
          <button type="button" onClick={() => void rescatarFallidas()} disabled={enviando}
            className="rounded bg-red-600 px-3 py-1 text-white disabled:opacity-50">
            Volver a intentar
          </button>
        </div>
      )}

      {mensaje !== '' && (
        <p className="mb-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
          {mensaje}
        </p>
      )}
      {error !== '' && (
        <p className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </p>
      )}

      {/* A · Identificación. Se pregunta UNA vez y se conserva puesto tras puesto. */}
      <Bloque titulo="¿En qué mercado estás?">
        {campanias.length > 1 && (
          <label className="mb-3 flex flex-col text-sm">
            Campaña
            <select className="mt-1 min-h-[44px] rounded border px-3 text-base" value={campaignId}
              onChange={(e) => { setCampaignId(e.target.value); setPdvId(''); }}>
              <option value="">— Elige —</option>
              {campanias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </label>
        )}
        <select className="min-h-[44px] w-full rounded border px-3 text-base"
          value={pdvId} onChange={(e) => setPdvId(e.target.value)}>
          <option value="">— Elige el mercado —</option>
          {mercados.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          {/* Para el mercado que no está en la lista. No crea un PDV: el maestro se administra
              aparte, y dejar que el campo lo llene produciría veinte variantes del mismo
              mercado escritas distinto. */}
          <option value={MERCADO_OTRO}>Otro mercado (escribirlo)</option>
        </select>
        {pdvId === MERCADO_OTRO && (
          <label className="mt-2 flex flex-col text-sm">
            ¿Qué mercado es?
            <input className="mt-1 min-h-[44px] rounded border px-3 text-base"
              placeholder="Nombre del mercado y su distrito"
              value={mercadoLibre} onChange={(e) => setMercadoLibre(e.target.value)} />
          </label>
        )}
        {mercados.length === 0 && campaignId !== '' && (
          <p className="mt-2 text-xs text-amber-700">
            Esta campaña todavía no tiene mercados cargados. Avísale a tu coordinador.
          </p>
        )}
        {marcas.length === 0 && campaignId !== '' && (
          <p className="mt-2 text-xs text-amber-700">
            Esta campaña todavía no tiene marcas configuradas: sin esa lista la ficha no puede
            preguntar por la cuadrícula.
          </p>
        )}
      </Bloque>

      {/* La vista de avance va ARRIBA del formulario y plegada: el conteo se lee de pasada al
          empezar cada puesto —que es cuando sirve— sin costarle una pantalla de desplazamiento a
          quien solo vino a llenar la ficha. Se muestra apenas hay mercado elegido, incluso si la
          ficha todavía no pudo armarse (por ejemplo, si la campaña no tiene marcas cargadas): lo
          registrado hoy no depende de eso. */}
      {claveMercado !== '' && (
        <BloqueAvance visitas={avance} abierto={avanceAbierto} servidorCaido={servidorCaido}
          onAlternar={() => setAvanceAbierto((v) => !v)} />
      )}

      {b === null ? (
        <p className="rounded border bg-white p-4 text-sm text-gray-600">
          Elige el mercado y empieza por el primer puesto de tu recorrido.
        </p>
      ) : (
        // La clave remonta el formulario entero al guardar: ver `fichaSeq`. Sin ella, los campos
        // que se despliegan solos arrancan abiertos con lo del puesto anterior.
        <Fragment key={fichaSeq}>

          {/* PASO 1 · El puesto: quién es, si se pudo medir y —plegado— con quién se habló. */}
          <Bloque titulo="¿Qué puesto es?"
            observacion={<Observacion valor={b.observaciones.obsPuesto}
              onChange={(v) => setObs('obsPuesto', v)} />}>
            {/* El identificador interno sigue siendo `numeroRecorrido` (y la columna
                `numero_recorrido`) aunque el nombre ya no describa lo que guarda: renombrarlo
                tocaría la RPC de producción, que además no está versionada en el repo. Se
                cambia lo que ve el auditor y el nombre viejo queda como deuda anotada. */}
            <label className="flex flex-col text-sm">
              Número del puesto, tal como está en el mercado
              <input type="number" inputMode="numeric" min={1}
                className="mt-1 min-h-[44px] rounded border px-3 text-base"
                value={b.numeroRecorrido}
                onChange={(e) => set('numeroRecorrido', e.target.value)} />
              <span className="mt-1 text-xs text-gray-500">
                El del letrero o el de su ubicación. No es el orden de tu recorrido.
              </span>
            </label>

            {/* Campo OPCIONAL y de referencia, no una pregunta del cuestionario. Existe porque el
                número no identifica —se repite y se saltea— y es lo que hace legible la lista de
                avance: «Puesto 1» dos veces no dice nada, «Puesto 1 · el de doña Rosa» sí. */}
            <label className="mt-3 flex flex-col text-sm">
              ¿Cómo lo reconocerías? <span className="text-xs text-gray-500">(opcional)</span>
              <input className="mt-1 min-h-[44px] rounded border px-3 text-base"
                placeholder="Ej.: el de doña Rosa, esquina pasillo 3"
                value={b.nombrePuesto}
                onChange={(e) => set('nombrePuesto', e.target.value)} />
              <span className="mt-1 text-xs text-gray-500">
                Una referencia tuya para reconocerlo después en tu lista de avance. No es una
                pregunta del cuestionario.
              </span>
            </label>

            {/* Avisa, NO bloquea: repetir un puesto es una de las dos fallas que arruinan el
                dato (la otra es saltárselo), pero puede haber razones legítimas y en este
                proyecto las banderas son calidad de dato a la vista, no candados —el mismo
                criterio que en los canjes—. Ya pasó en producción: dos puestos con el n.º 1 en
                Mercado Unicachi Norte el mismo día.

                Desde el 19-ago mira TODO lo que esta CUENTA registró hoy en el mercado —lo del
                servidor y lo que sigue en la cola de este celular—, así que también ve lo medido
                desde otro teléfono o en una sesión anterior del mismo día. */}
            {numeroRepetido && (
              <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Ya registraste hoy un puesto n.º {b.numeroRecorrido.trim()} en este mercado. Si
                es otro puesto, corrige el número; si de verdad es el mismo, continúa. (Este aviso
                mira lo registrado con TU cuenta: lo que otra persona haya medido acá con la suya
                no se ve.)
              </p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Recuerda: mides cada tercer puesto que venda la categoría, recorriendo el pasillo
              de punta a punta.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Opcion activo={medido} onClick={() => set('estado', 'medido')}>Sí pude medir</Opcion>
              <Opcion activo={!medido} onClick={() => set('estado', 'no_pude')}>No pude</Opcion>
            </div>
            {!medido && (
              <div className="mt-3 grid gap-2">
                {MOTIVOS_NO_PUDE.map((m) => (
                  <Opcion key={m.valor} activo={b.motivoNoPude === m.valor}
                    onClick={() => set('motivoNoPude', m.valor)}>{m.label}</Opcion>
                ))}
              </div>
            )}

            {/* CONTACTO DEL PUESTERO — opcional, plegado y con el para qué a la vista.
                ⚠️ Es dato personal de un TERCERO: el puestero no es usuario del sistema y no
                aceptó nada. Va plegado por dos razones a la vez: cuesta cero toques al que no lo
                pide (que es la cuenta que decide si esta ficha sirve) y no invita a llenarlo por
                inercia. La línea del para qué no es cortesía: es lo que el auditor le va a decir
                al puestero cuando le pregunte, y sin ella cada uno inventa la suya.
                En el servidor esas dos columnas tienen la lectura revocada y solo salen por una
                función auditada (0060); acá viven en claro nada más el rato que dura la ficha. */}
            {medido && (
              <Plegable
                etiqueta="+ Anotar el contacto del puestero (opcional)"
                abiertoInicial={b.contactoNombre !== '' || b.contactoTelefono !== ''}>
                <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs text-gray-600">
                    Sirve para que el equipo comercial pueda volver a buscarlo (una activación, una
                    promoción para su puesto). No sale en ninguna lista y solo lo abre quien
                    administra el levantamiento. Si no quiere darlo, se sigue igual.
                  </p>
                  <label className="mt-2 flex flex-col text-sm">
                    ¿Cómo se llama? <span className="text-xs text-gray-500">(opcional)</span>
                    <input className="mt-1 min-h-[44px] rounded border px-3 text-base"
                      placeholder="Nombre del puestero"
                      value={b.contactoNombre}
                      onChange={(e) => set('contactoNombre', e.target.value)} />
                  </label>
                  <label className="mt-2 flex flex-col text-sm">
                    Su teléfono <span className="text-xs text-gray-500">(opcional)</span>
                    <input type="tel" inputMode="tel"
                      className="mt-1 min-h-[44px] rounded border px-3 text-base"
                      placeholder="9 dígitos"
                      value={b.contactoTelefono}
                      onChange={(e) => set('contactoTelefono', e.target.value)} />
                  </label>
                </div>
              </Plegable>
            )}
          </Bloque>

          {medido && (
            <>
              {/* PASO 2 · Qué vende. LA pregunta que recorta todo lo que sigue. Reemplaza al
                  `giro` de opción única, que obligaba a elegir entre «papel» y «pañal» a los 12
                  puestos de 22 que venden las dos cosas — y que por eso no servía para filtrar
                  nada. `giro` sigue viajando: lo deriva `visitaDesdeBorrador` de estas casillas,
                  porque el CHECK del servidor todavía lo exige. */}
              <Bloque titulo="¿Qué vende este puesto?"
                observacion={<Observacion valor={b.observaciones.obsGiro}
                  onChange={(v) => setObs('obsGiro', v)} />}>
                <p className="mb-2 text-xs text-gray-500">
                  Marca todas las que venda. Solo se te van a preguntar las marcas de lo que
                  marques acá.
                </p>

                {/* El atajo del recorrido. 73 % de los puestos declaran lo mismo que el anterior
                    (ver `categoriasDelPuestoAnterior`), así que sin esto el caso más común costaba
                    DOS toques donde el formulario viejo cobraba uno. Se OFRECE, no se precarga: el
                    auditor toca y declara. Desaparece cuando ya coincide, para no ofrecer un botón
                    que no haría nada. */}
                {categoriasAnteriores.length > 0 && !mismasCategoriasQueElAnterior && (
                  <button type="button" onClick={copiarDelAnterior}
                    className="mb-2 min-h-[44px] w-full rounded border border-blue-600 px-3 text-sm
                               font-medium text-blue-700">
                    Lo mismo que el puesto anterior:{' '}
                    {categoriasAnteriores.map(etiquetaCategoria).join(' + ')}
                  </button>
                )}

                <div className="grid gap-2">
                  {CATEGORIAS.map((c) => {
                    const puesta = b.categorias.includes(c.valor);
                    return (
                      <label key={c.valor}
                        className={`flex min-h-[52px] cursor-pointer items-center gap-3 rounded border px-3 py-2 text-base ${
                          puesta ? 'border-blue-600 bg-blue-50 font-medium text-blue-900' : 'border-gray-300'
                        }`}>
                        <input type="checkbox" className="h-6 w-6 shrink-0"
                          checked={puesta} onChange={() => alternar(c.valor)} />
                        {c.label}
                      </label>
                    );
                  })}
                </div>

                {/* Destildar una categoría no borra lo contestado —volver a tildarla lo devuelve—
                    pero SÍ deja de guardarlo (`borradorEfectivo`). Callarlo sería perder trabajo
                    en silencio, que es peor que perderlo con un aviso. */}
                {huerfanas.length > 0 && (
                  <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Tienes respuestas de {huerfanas.map(etiquetaCategoria).join(' y ')} y este
                    puesto ya no la vende, así que no se van a guardar. Si fue un error, vuelve a
                    marcarla y las recuperas.
                  </p>
                )}
              </Bloque>

              {declaroCategorias && (
                <>
                  <Bloque titulo="¿A quién le vende más este puesto?"
                    observacion={<Observacion valor={b.observaciones.obsCliente}
                      onChange={(v) => setObs('obsCliente', v)} />}>
                    <div className="grid gap-2">
                      {CLIENTES.map((c) => (
                        <Opcion key={c.valor} activo={b.clientePrincipal === c.valor}
                          onClick={() => set('clientePrincipal', c.valor)}>{c.label}</Opcion>
                      ))}
                    </div>
                  </Bloque>

                  {/* PASO 3 · La cuadrícula, UNA SECCIÓN POR CATEGORÍA y ninguna más. Un puesto
                      que solo marcó «papel higiénico» no ve ni una pregunta de pañal. */}
                  <Bloque titulo="Marca por marca, ¿qué tiene hoy este puesto?"
                    observacion={<Observacion valor={b.observaciones.obsCuadricula}
                      onChange={(v) => setObs('obsCuadricula', v)} />}>
                    <div className="grid gap-5">
                      {b.categorias.map((c) => {
                        const deLaCategoria = marcasDeCategorias(marcas, [c]);
                        return (
                          <div key={c}>
                            <h3 className="mb-2 border-b pb-1 text-sm font-semibold text-gray-800">
                              {etiquetaCategoria(c)}
                            </h3>
                            {deLaCategoria.length === 0 ? (
                              // Caso real hoy: `toallas_higienicas` no tiene ni una marca
                              // configurada. Sin este texto la sección sale vacía y se lee como si
                              // la aplicación estuviera rota; con él, el auditor sabe por dónde
                              // entra ese dato.
                              <p className="rounded border border-dashed border-gray-300 px-3 py-3 text-xs text-gray-600">
                                Esta campaña todavía no tiene marcas de {etiquetaCategoria(c).toLowerCase()}
                                {' '}cargadas. Anota lo que veas más abajo, en «Precios», con
                                {' '}<strong>«+ Agregar el precio de otro producto»</strong>, y escribe
                                {' '}ahí el nombre de la marca.
                              </p>
                            ) : (
                              <div className="grid gap-3">
                                {deLaCategoria.map((m) => (
                                  <div key={m.id}>
                                    <p className="mb-1 text-sm font-medium">{m.nombre}</p>
                                    <div className="grid grid-cols-3 gap-1.5">
                                      {OPCIONES_PRESENCIA.map((o) => (
                                        <Opcion key={o.valor} centrado
                                          activo={b.presencias[m.id] === o.valor}
                                          onClick={() => setPresencia(m.id, o.valor)}>
                                          {o.label}
                                        </Opcion>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Bloque>

                  {/* PASO 4 · Precio, SOLO de las marcas que dijo «Tiene». Eso limita el trabajo
                      solo: 5 marcas presentes de 13 son 5 precios, no 13. */}
                  <Bloque titulo="Precios y presentaciones"
                    observacion={<Observacion valor={b.observaciones.obsPrecios}
                      onChange={(v) => setObs('obsPrecios', v)} />}>
                    {/* Sin este aviso la sección se ve vacía y parece rota: las filas se proponen
                        solas al marcar «Tiene» arriba, y eso no se adivina mirando. */}
                    {grupos.length === 0 ? (
                      <p className="rounded border border-dashed border-gray-300 px-3 py-3 text-xs text-gray-600">
                        Todavía no hay ninguno. Se agregan solos cuando marcas una marca como
                        <strong> «Tiene»</strong> arriba — o agrégalo tú con el botón de abajo,
                        para cualquier producto del puesto.
                      </p>
                    ) : (
                      <p className="mb-3 text-xs text-gray-500">
                        Ninguno es obligatorio. Una fila por cada presentación con precio distinto:
                        si el mismo producto se vende suelto y por paquete, son dos.
                      </p>
                    )}

                    <div className="grid gap-5">
                      {grupos.map((g) => (
                        <div key={g.clave}>
                          <h3 className="mb-2 border-b pb-1 text-sm font-semibold text-gray-800">
                            {g.titulo}
                          </h3>
                          {!g.declarada && (
                            <p className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                              Este puesto ya no vende esta categoría, así que estos precios no se
                              van a guardar. Vuelve a marcarla arriba o quita las filas.
                            </p>
                          )}
                          <div className="grid gap-4">
                            {g.filas.map((p) => (
                              <FilaDePrecio
                                key={p.id}
                                fila={p}
                                marca={marcas.find((m) => m.id === p.marcaId)}
                                presentaciones={presentacionesDeMarca(presentaciones, p.marcaId)}
                                categorias={b.categorias}
                                onCambio={(cambio) => setPrecio(p.id, cambio)}
                                onQuitar={() => set('precios', b.precios.filter((x) => x.id !== p.id))}
                                onOtroPrecio={() => set('precios', [
                                  ...b.precios, filaPrecioVacia(nuevoId(), p.marcaId),
                                ])}
                                onFotos={(files) => sumarFotos(files, enFotosPrecio(p.id))}
                                onQuitarFoto={(i) => enFotosPrecio(p.id)((ya) => quitarEn(ya, i))}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <button type="button"
                      onClick={() => set('precios', [...b.precios, filaPrecioVacia(nuevoId())])}
                      className="mt-3 block min-h-[44px] w-full rounded border border-blue-300 bg-blue-50 text-sm font-medium text-blue-800">
                      + Agregar el precio de otro producto
                    </button>
                  </Bloque>

                  {/* PASO 5 · Lo cualitativo, una vez al final. Promociones (filas repetibles,
                      propias y de la competencia), recomendación, foto y marca nueva. */}
                  <Bloque titulo="¿Hoy alguna marca está dando algo de regalo o en promoción aquí?"
                    observacion={<Observacion valor={b.observaciones.obsPromocion}
                      onChange={(v) => setObs('obsPromocion', v)} />}>
                    <div className="grid grid-cols-2 gap-2">
                      <Opcion activo={b.hayPromocion === true}
                        onClick={() => setB({
                          ...b, hayPromocion: true,
                          promociones: b.promociones.length === 0
                            ? [filaPromocionVacia(nuevoId())] : b.promociones,
                        })}>Sí</Opcion>
                      <Opcion activo={b.hayPromocion === false}
                        onClick={() => set('hayPromocion', false)}>No</Opcion>
                    </div>

                    {b.hayPromocion === true && (
                      <>
                        <div className="mt-3 grid gap-4">
                          {b.promociones.map((p) => (
                            <div key={p.id} className="rounded border border-gray-200 p-3">
                              <div className="mb-2 flex items-start justify-between gap-2">
                                <p className="text-sm font-medium">Promoción</p>
                                <button type="button"
                                  onClick={() => set('promociones', b.promociones.filter((x) => x.id !== p.id))}
                                  className="min-h-[32px] text-xs text-red-600 underline">quitar</button>
                              </div>

                              <div className="grid grid-cols-2 gap-2">
                                {DE_QUIEN.map((d) => (
                                  <Opcion key={d.valor} activo={p.deQuien === d.valor}
                                    onClick={() => setPromo(p.id, { deQuien: d.valor })}>{d.label}</Opcion>
                                ))}
                              </div>

                              {/* Solo las marcas de lo que este puesto vende: preguntar por una
                                  promoción de pañal en un puesto que declaró que no los vende es
                                  la misma pregunta de más que el rediseño vino a sacar. La opción
                                  de escribirla cubre lo que no está en la lista. */}
                              <label className="mt-2 flex flex-col text-sm">
                                ¿De qué marca?
                                <select className="mt-1 min-h-[44px] rounded border px-3 text-base"
                                  value={p.marcaId}
                                  onChange={(e) => setPromo(p.id, { marcaId: e.target.value })}>
                                  <option value="">— Otra marca (escríbela abajo) —</option>
                                  {marcasVisibles.map((m) => (
                                    <option key={m.id} value={m.id}>{m.nombre}</option>
                                  ))}
                                </select>
                              </label>
                              {p.marcaId === '' && (
                                <input className="mt-2 min-h-[44px] w-full rounded border px-3 text-base"
                                  placeholder="Nombre de la marca"
                                  value={p.marcaLibre}
                                  onChange={(e) => setPromo(p.id, { marcaLibre: e.target.value })} />
                              )}

                              <label className="mt-2 flex flex-col text-sm">
                                ¿Qué está dando?
                                <input className="mt-1 min-h-[44px] rounded border px-3 text-base"
                                  placeholder="Ej.: regala paños húmedos por 2 paquetes"
                                  value={p.descripcion}
                                  onChange={(e) => setPromo(p.id, { descripcion: e.target.value })} />
                              </label>

                              <CampoFotos
                                etiqueta="Fotos de la promoción (opcional, puedes subir varias)"
                                nombre="las fotos de esta promoción"
                                fotos={p.fotos}
                                onAgregar={(files) => sumarFotos(files, enFotosPromo(p.id))}
                                onQuitar={(i) => enFotosPromo(p.id)((ya) => quitarEn(ya, i))} />
                            </div>
                          ))}
                        </div>
                        <button type="button"
                          onClick={() => set('promociones', [...b.promociones, filaPromocionVacia(nuevoId())])}
                          className="mt-3 block min-h-[44px] w-full rounded border border-blue-300 bg-blue-50 text-sm font-medium text-blue-800">
                          + Agregar otra promoción
                        </button>
                      </>
                    )}
                  </Bloque>

                  <Bloque titulo="Si entra un cliente y no pide marca, ¿cuál le ofreces primero?"
                    observacion={<Observacion valor={b.observaciones.obsRecomendacion}
                      onChange={(v) => setObs('obsRecomendacion', v)} />}>
                    {b.recomendacionDeclinada ? (
                      <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                        Anotado: no quiso responder.
                        <button type="button" className="ml-2 underline"
                          onClick={() => set('recomendacionDeclinada', false)}>Deshacer</button>
                      </div>
                    ) : (
                      <>
                        {/* Opción única + Otra, no texto libre: es "share de recomendación" y se
                            CUENTA. Con texto libre «Babysec» y «baby sec» son dos marcas.
                            ⚠️ Esta lista NO se recorta por categoría: es una constante del dominio
                            escrita a mano (`MARCAS_RECOMENDABLES`) y no las marcas de la campaña,
                            así que no tiene de dónde sacar a qué categoría pertenece cada una.
                            Recortarla pide moverla a la configuración de campaña. */}
                        <div className="grid gap-2">
                          {[...MARCAS_RECOMENDABLES, RECOMENDADA_OTRA].map((m) => (
                            <Opcion key={m} activo={b.marcaRecomendada === m}
                              onClick={() => set('marcaRecomendada', m)}>{m}</Opcion>
                          ))}
                        </div>
                        {b.marcaRecomendada === RECOMENDADA_OTRA && (
                          <input className="mt-2 min-h-[44px] w-full rounded border px-3 text-base"
                            placeholder="¿Cuál?"
                            value={b.marcaRecomendadaOtra}
                            onChange={(e) => set('marcaRecomendadaOtra', e.target.value)} />
                        )}

                        <p className="mb-2 mt-3 text-sm">¿Por qué esa primero?</p>
                        <div className="grid gap-2">
                          {MOTIVOS_RECOMENDACION.map((m) => (
                            <Opcion key={m.valor} activo={b.motivoRecomendacion === m.valor}
                              onClick={() => set('motivoRecomendacion', m.valor)}>{m.label}</Opcion>
                          ))}
                        </div>
                        {/* Sin este botón, el auditor apurado INVENTA una respuesta antes que dejar
                            el campo vacío. Un dato inventado es peor: no se distingue. */}
                        <button type="button" onClick={() => set('recomendacionDeclinada', true)}
                          className="mt-3 min-h-[44px] w-full rounded border border-gray-300 text-sm text-gray-700">
                          No quiso responder
                        </button>
                      </>
                    )}
                  </Bloque>

                  <Bloque titulo="Para cerrar: la foto y lo que no estaba en la lista"
                    observacion={<Observacion valor={b.observaciones.obsEvidencia}
                      onChange={(v) => setObs('obsEvidencia', v)} />}>
                    {/* La única foto OBLIGATORIA de la ficha, y por eso mismo la que más necesita
                        el camino de la galería: si la cámara falla o el puestero no deja
                        fotografiar en el momento, sin ese segundo camino la ficha entera se queda
                        sin poder guardarse. Sigue siendo un dato, no una prueba de presencia: lo
                        que ata la ficha al terreno es el GPS que se pide al guardar. */}
                    <CampoFotos
                      etiqueta="Foto del puesto — el frente completo, como se ve al pasar"
                      nombre="las fotos del puesto"
                      fotos={b.fotoPuesto}
                      onAgregar={(files) => sumarFotos(files, enFotoPuesto)}
                      onQuitar={(i) => enFotoPuesto((ya) => quitarEn(ya, i))}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Con una alcanza para guardar. Si el frente no entra en una sola toma, suma las
                      que necesites.
                    </p>

                    {/* Esta pregunta NO está en el Excel: viene del documento de diseño y se
                        conserva porque es lo único que descubre marcas que nadie puso en la lista.
                        Va como opcional para no alargar la ficha. */}
                    <p className="mb-2 mt-4 text-sm">
                      ¿Viste alguna marca que no conoces? <span className="text-xs text-gray-500">(opcional)</span>
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <Opcion activo={b.vioMarcaDesconocida === true}
                        onClick={() => set('vioMarcaDesconocida', true)}>Sí</Opcion>
                      <Opcion activo={b.vioMarcaDesconocida === false}
                        onClick={() => set('vioMarcaDesconocida', false)}>No</Opcion>
                    </div>
                    {b.vioMarcaDesconocida === true && (
                      <>
                        <label className="mt-3 flex flex-col text-sm">
                          Nombre tal como se lee en el empaque
                          <input className="mt-1 min-h-[44px] rounded border px-3 text-base"
                            value={b.hallazgoNombre}
                            onChange={(e) => set('hallazgoNombre', e.target.value)} />
                          <span className="mt-1 text-xs text-gray-500">
                            Una sola marca. Si viste varias, la que más te llamó la atención va acá
                            y las otras van con su precio, cada una en su fila.
                          </span>
                        </label>
                        {/* ⚠️ El defecto exacto del primer día real: entró
                            «Royal, doñita y vitale» en este campo y esas tres marcas no se pueden
                            contar ni dar de alta. Avisa y no bloquea, como todas las banderas de
                            este sistema: puede haber un nombre legítimo con una coma. */}
                        {avisoVariasMarcasEnUnCampo(b.hallazgoNombre) !== null && (
                          <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            {avisoVariasMarcasEnUnCampo(b.hallazgoNombre)}
                          </p>
                        )}
                        <CampoFotos
                          etiqueta="Fotos del empaque (opcional, puedes subir varias)"
                          nombre="las fotos del empaque"
                          fotos={b.fotoHallazgo}
                          onAgregar={(files) => sumarFotos(files, enFotoHallazgo)}
                          onQuitar={(i) => enFotoHallazgo((ya) => quitarEn(ya, i))} />
                        <p className="mt-1 text-xs text-gray-500">
                          La foto es lo que después permite saber si «Besper», «Vesper» y «Veesper»
                          son la misma marca, sin adivinar.
                        </p>
                      </>
                    )}
                  </Bloque>
                </>
              )}
            </>
          )}

          <p className="mb-4 text-center text-sm">
            <Link
              to={`/movil/cierre-jornada?pdv=${pdvId === MERCADO_OTRO ? '' : pdvId}`
                + `&libre=${encodeURIComponent(pdvId === MERCADO_OTRO ? mercadoLibre.trim() : '')}`}
              className="text-blue-600 underline">
              Terminé en este mercado: cerrar la jornada
            </Link>
          </p>

          <div className="fixed inset-x-0 bottom-0 border-t bg-white p-3">
            <button type="button" onClick={() => void guardar()} disabled={enviando}
              className="min-h-[48px] w-full rounded bg-blue-600 text-base font-medium text-white disabled:opacity-50">
              {/* Sin número el botón dice solo «Guardar puesto»: ahora el campo nace vacío y
                  «Guardar puesto » con el hueco al final se lee como un texto roto. */}
              {enviando
                ? 'Guardando…'
                : b.numeroRecorrido.trim() === ''
                  ? 'Guardar puesto'
                  : `Guardar puesto ${b.numeroRecorrido.trim()}`}
            </button>
          </div>
        </Fragment>
      )}
    </div>
  );
}
